import { z } from 'zod'
import { FEATURE_REGISTRY } from './feature-registry.js'

export const userRole = z.enum(['super_admin', 'company_admin', 'sub_admin', 'user'])
export const userStatus = z.enum(['invited', 'active', 'suspended', 'offboarded'])
/** Sub-role inside the user panel: Manager > Team Leader > Employee. */
export const workRole = z.enum(['manager', 'team_leader', 'employee'])

export const sessionUser = z.object({
  id: z.string().uuid(),
  /** Null for super_admin (platform-level, no tenant). */
  tenantId: z.string().uuid().nullable(),
  emailId: z.string().email(),
  fullName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  role: userRole,
  /** [External Collaboration] internal | external trust boundary. Drives
   *  hiding of internal-only surfaces (e.g. Files & Links) for externals
   *  and the external-aware branches in the share dialog / Files page /
   *  report settings. MUST be declared here — Zod strips undeclared
   *  response keys, so without it `membershipType` never reaches the store
   *  and every external is treated as internal. Optional with an
   *  `internal` default so older API responses parse cleanly. Typed as a
   *  plain string (not an enum) so an unexpected value can never throw and
   *  lock the user out of the whole app on this critical /auth/me payload —
   *  every consumer only checks `=== 'external'`, so anything else safely
   *  reads as internal. */
  membershipType: z.string().optional().default('internal'),
  /** Does this account have a PASSWORD credential? Server-derived from the
   *  global Identity; the hash itself never crosses the wire. Distinct from
   *  `googleLinked` — an account can have both, so being Google-linked does
   *  NOT imply password-less. Defaults TRUE so an older API response (or any
   *  payload predating this field) keeps the stricter behaviour: the
   *  current-password proof stays required rather than silently dropping. */
  hasPassword: z.boolean().optional().default(true),
  workRole: workRole.optional().default('employee'),
  // Work Role Management fields — drive every per-feature gate on
  // the frontend (sidebar visibility, button enabled-state, page
  // redirects). The backend resolves these in `loadActorContext`,
  // which falls back to `DEFAULT_OPERATIONS_BY_LEVEL[workRole]` when
  // the user has no explicit WorkRole row yet — so this payload is
  // safe to consume even on tenants that haven't customised their
  // Work Role catalogue.
  workRoleId: z.string().uuid().nullable().optional(),
  workRoleName: z.string().nullable().optional(),
  /** Hierarchy level slug (manager / team_leader / employee). The
   *  backend may also send the legacy `workRoleTier` field with the
   *  same value — both are accepted here so a deploy mismatch
   *  doesn't break the parse. */
  workRoleLevel: workRole.nullable().optional(),
  workRoleLevelLabel: z.string().nullable().optional(),
  /** @deprecated Back-compat alias for `workRoleLevel`. */
  workRoleTier: workRole.nullable().optional(),
  /** Effective operation keys for this user. Source of truth for
   *  every UI gate — page access, button availability, etc. */
  operations: z.array(z.string()).optional().default([]),
  /** Sub Admin delegated operation slugs (e.g. `members.manage`,
   *  `audit_log.view`). Only populated when `role === 'sub_admin'`.
   *  Company Admins have universal access regardless of this field. */
  subAdminOperations: z.array(z.string()).optional().default([]),
  /** Google SSO flags.
   *
   *  `profileComplete` is true for classic email/password accounts (they
   *  filled the long sign-up form inline) and for Google accounts after
   *  the user submits the Complete Profile modal. While false, the
   *  dashboard renders a non-dismissable modal locking interaction.
   *
   *  `googleLinked` is true once a Google `sub` has been recorded on
   *  the user — drives "Connected with Google" pills + skipping
   *  password-reset for SSO-only accounts. Both default to safe values
   *  so older API responses still parse. */
  profileComplete: z.boolean().optional().default(true),
  googleLinked: z.boolean().optional().default(false),
})

// Login Activity Logs — one row per user with derived Online/Offline
// status. Tenant-scoped server-side. `lastLoginAt` / `lastLogoutAt` /
// `lastActiveAt` are ISO datetime strings (or null). `lastSignInIp` is
// the raw IP — null when not captured (private network, localhost).
export const loginActivityRow = z.object({
  id: z.string(),
  fullName: z.string(),
  emailId: z.string().email(),
  role: userRole,
  workRole: workRole.optional(),
  /** Assigned Work Role catalogue NAME (e.g. "Team Leader"). Null when the
   *  user has no custom Work Role; the page falls back to the tier label.
   *  Shown in the Role column for non-admin users. */
  workRoleName: z.string().nullable().optional(),
  lastLoginAt: z.string().nullable(),
  lastLogoutAt: z.string().nullable(),
  lastActiveAt: z.string().nullable(),
  lastSignInIp: z.string().nullable(),
  accountStatus: userStatus,
  status: z.enum(['online', 'offline']),
  failedLoginAttempts: z.number().int().nonnegative().optional().default(0),
  lastFailedLoginAt: z.string().nullable().optional(),
  // True when either the live counter is > 0 OR `lastFailedLoginAt`
  // falls within the server-side window (24h). Lets the page render
  // a "recent fail" badge without re-deriving the same logic.
  recentlyFailed: z.boolean().optional().default(false),
})
export const loginActivityResponse = z.object({
  data: z.array(loginActivityRow),
  counts: z.object({
    all: z.number().int().nonnegative(),
    online: z.number().int().nonnegative(),
    offline: z.number().int().nonnegative(),
    // Number of USERS with a failed/recent-failed sign-in (the 'failed' filter).
    failed: z.number().int().nonnegative().optional().default(0),
    // TOTAL failed sign-in attempts from the permanent log — the "Failed
    // Attempts" card. Optional/defaulted so older responses still parse.
    failedAttempts: z.number().int().nonnegative().optional().default(0),
  }),
})

// Drill-down: individual failed sign-in records for one user.
export const loginFailureRecord = z.object({
  id: z.string(),
  at: z.string().nullable(),
  emailId: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
})
export const loginFailuresResponse = z.object({
  user: z
    .object({
      id: z.string(),
      fullName: z.string().nullable().optional(),
      emailId: z.string().nullable().optional(),
    })
    .optional(),
  data: z.array(loginFailureRecord),
})

// Super Admin variant — ONE ROW PER ORGANISATION, shaped to mirror
// the per-user row layout the Company Admin page uses so the same
// table chrome renders unchanged. Each row aggregates the tenant's
// users into the same fields a single member would expose.
export const platformOrgActivityRow = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  planTier: z.string().nullable().optional(),
  totalUsers: z.number().int().nonnegative(),
  onlineCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  lastLoginAt: z.string().nullable(),
  lastLogoutAt: z.string().nullable(),
  lastActiveAt: z.string().nullable(),
  lastSignInIp: z.string().nullable(),
  // Mirrors the CA-side shape so the same column components render.
  status: z.enum(['online', 'offline']),
  recentlyFailed: z.boolean(),
})

export const platformOrgActivityResponse = z.object({
  data: z.array(platformOrgActivityRow),
  counts: z.object({
    all: z.number().int().nonnegative(),
    online: z.number().int().nonnegative(),
    offline: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
})

// Public Google SSO config — consumed by the Sign In / Sign Up pages
// to decide whether to render the "Continue with Google" button.
// Returned by GET /auth/google/config. Secret is never on the wire.
export const googleSsoPublicConfig = z.object({
  enabled: z.boolean(),
  clientId: z.string(),
})

// Super Admin Google SSO config — adds the boolean "secret on file"
// signal. Returned by GET /super-admin/google-sso.
export const googleSsoAdminConfig = z.object({
  enabled: z.boolean(),
  clientId: z.string(),
  clientSecretSet: z.boolean(),
  // Backend callback URL (derived from API_URL) to paste into Google
  // Cloud Console. Optional so an older API that doesn't send it parses.
  redirectUri: z.string().optional(),
  updatedAt: z.string().nullable().optional(),
  updatedById: z.string().nullable().optional(),
})

// Tenant plan tier is now a free-form string slug — the Plan
// catalogue (managed by Platform Admin → Plans page) is the source
// of truth for valid codes. Locking this to an enum would mean the
// frontend can't parse a response for a tenant on a custom plan
// (`starter`, `growth`, …) until we redeploy. Validation that the
// code actually exists lives server-side; the Sign-up picker only
// shows real codes.
// Empty string is a valid value — it means "no plan selected yet"
// (the tenant must pick a Master-Admin plan on /billing). The UI shows
// a "No plan" pill / "No plan selected" for it.
export const tenantPlanTier = z.string().max(50)

// Derived from the single feature registry (lib/feature-registry.js).
//   - SHIPPED (off-by-default) flags — sop_integration, external_collaboration
//     — parse to an explicit `false` when absent, so `hasFeature` FAILS CLOSED
//     and keeps the surface hidden until a plan turns it on.
//   - PLAN-GATED flags stay FAIL-OPEN (`.optional()`): an absent key is
//     `undefined` → enabled, which grandfathers legacy tenants. Only an
//     explicit `false` (a plan that excludes the feature) blocks.
export const tenantFeatures = z.object(
  Object.fromEntries(
    FEATURE_REGISTRY.map((f) => [
      f.key,
      f.gate === 'shipped' ? z.boolean().default(false) : z.boolean().optional(),
    ]),
  ),
)

/** Billing-lock state for the tenant. `locked` is true when the tenant
 *  is on a paid plan that hasn't been paid for yet — the Company Admin
 *  panel gates everything but Billing behind the Pay-Now flow. */
export const tenantBilling = z.object({
  locked: z.boolean().optional().default(false),
  status: z.string().nullable().optional(),
  planCode: z.string().nullable().optional(),
  planName: z.string().nullable().optional(),
  planType: z.string().nullable().optional(),
  amount: z.number().nullable().optional(),
  currency: z.string().optional().default('USD'),
  /** Promo code captured at sign-up, not yet redeemed. The Billing
   *  page pre-fills + previews it so the customer sees the discount
   *  that auto-applies on checkout. Null / absent when none. */
  pendingCouponCode: z.string().nullable().optional(),
})

export const sessionTenant = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  /** Optional + nullable so old responses without this field don't break
   *  the parse — the auth-store falls back to default features. */
  planTier: tenantPlanTier.optional(),
  features: tenantFeatures.optional(),
  /** Optional + nullable so older responses without billing still parse. */
  billing: tenantBilling.nullable().optional(),
  /** ISO 3166-1 alpha-2 company country (e.g. 'IN', 'NL'), used by the
   *  My Preferences "Company country" editor. Independent of billing
   *  currency / tax / timezone. Optional so older responses still parse. */
  country: z.string().length(2).nullable().optional(),
  /** IANA timezone the report-reminder runner interprets each schedule
   *  slot in (e.g. 'Asia/Kolkata'). Null = server-local fallback.
   *  Optional so older responses still parse. */
  timezone: z.string().nullable().optional(),
  /** Company-wide task-reminder ladder — minutes before a task's due
   *  time at which the runner sends reminders (e.g. [30, 1440] = 30 min
   *  + 1 day). `[]` = reminders off. Optional so older responses still
   *  parse; the Profile-page editor falls back to the default when absent. */
  taskReminderOffsets: z.array(z.number().int()).optional(),
  overdueReminderIntervalMinutes: z.number().int().optional(),
  /** First-plan-selected timestamp. Null when the tenant was
   *  bootstrapped via Google SSO but the Company Admin hasn't yet
   *  visited /billing and picked a plan. The SPA's PlanGate refuses
   *  every other route until this is non-null. Optional so older API
   *  responses still parse. */
  planSelectedAt: z.string().nullable().optional(),
})

/** Impersonation marker (§1). Present + non-null only when a Super Admin
 *  is viewing the app "as" another user — drives the sticky banner. Null
 *  / absent on every ordinary session. */
export const impersonationState = z
  .object({
    impersonatedBy: z.string().uuid(),
    adminName: z.string().nullable().optional(),
    adminEmail: z.string().nullable().optional(),
  })
  .nullable()
  .optional()

/** One entry in the account switcher — an account the current browser
 *  has authenticated (the person's own accounts across companies, plus
 *  any other logins added on this device). Fields kept permissive
 *  (z.string(), not .uuid()) so a backend tweak never silently drops the
 *  list via a strict-parse failure. */
export const accountEntry = z.object({
  userId: z.string(),
  tenantId: z.string().nullable().optional(),
  tenantName: z.string().nullable().optional(),
  fullName: z.string().nullable().optional(),
  emailId: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  workRoleName: z.string().nullable().optional(),
  isCurrent: z.boolean().optional().default(false),
  /** False when the account can't be switched into (suspended /
   *  offboarded / organisation unavailable). Defaults true so older
   *  responses without the field stay switchable. */
  available: z.boolean().optional().default(true),
  reason: z.string().nullable().optional(),
  /** True when this is the person's OWN pending invite — it's switchable,
   *  and switching into it activates (joins) the membership. */
  pending: z.boolean().optional().default(false),
})

/** Auth response no longer carries an access token — both tokens live in
 *  httpOnly cookies set by the API (taskmagix_access, taskmagix_refresh).
 *  `tenant` is null for super_admin sign-in. */
export const authResponse = z.object({
  user: sessionUser,
  tenant: sessionTenant.nullable(),
  impersonation: impersonationState,
  /** Account-switcher list for this browser. Optional/defaulted so the
   *  register/Google paths (which don't carry it) still parse. */
  availableAccounts: z.array(accountEntry).optional().default([]),
})

export const meResponse = z.object({
  user: sessionUser.extend({
    status: userStatus,
    contactNumber: z.string().nullable().optional(),
    /** Workspaces the caller belongs to. 0 → first-time User-panel onboarding. */
    workspaceCount: z.number().int().nonnegative().optional(),
    /** The caller's department (shown on My Profile). Null for tenant
     *  admins or a user with no department membership. */
    departmentId: z.string().uuid().nullable().optional(),
    departmentName: z.string().nullable().optional(),
  }),
  tenant: sessionTenant.nullable(),
  impersonation: impersonationState,
  /** Account-switcher list for this browser (see accountEntry). */
  availableAccounts: z.array(accountEntry).optional().default([]),
})

export const teammate = z.object({
  id: z.string().uuid(),
  emailId: z.string().email(),
  fullName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  role: userRole,
  /** User-panel work role — drives the Team Tasks Add Task picker so
   *  Managers see Team Leaders + Employees, Team Leaders see Employees. */
  workRole: workRole.optional().default('employee'),
  /** Friendly Work Role (designation) name — shown under the name in people
   *  pickers, matching the Space assignee picker. */
  workRoleName: z.string().nullable().optional(),
})

export const teammatesResponse = z.object({
  departmentId: z.string().uuid().nullable(),
  departmentName: z.string().nullable(),
  data: z.array(teammate),
})

export const workspaceSummary = z.object({
  id: z.string().uuid(),
  name: z.string(),
  avatarUrl: z.string().nullable().optional(),
  avatarColor: z.string().nullable().optional(),
  createdAt: z.string(),
  memberCount: z.number().int().nonnegative().optional(),
  role: z.enum(['admin', 'member', 'guest', 'external']).optional(),
  /** `active` or `inactive`. Surfaced so the Departments picker (and
   *  any other workspace selector for write-paths) can hide / dim
   *  Inactive workspaces. Defaults to `'active'` for legacy
   *  responses that don't carry the field yet. */
  status: z.enum(['active', 'inactive']).optional().default('active'),
  /** The actor's Department within this workspace (membership is
   *  per-workspace). Null = company-wide / no department, or a
   *  tenant-admin who isn't a department member. Optional so legacy
   *  responses that predate the field still parse. */
  departmentId: z.string().uuid().nullable().optional(),
  departmentName: z.string().nullable().optional(),
})

export const workspaceListResponse = z.object({
  data: z.array(workspaceSummary),
})

/** Tenant-wide workspace audit row returned from /workspaces/admin/all.
 *  Includes the workspace owner + per-workspace counts that the Company
 *  Admin Workspaces page renders in its table. */
export const tenantWorkspaceRow = z.object({
  id: z.string().uuid(),
  name: z.string(),
  avatarUrl: z.string().nullable().optional(),
  avatarColor: z.string().nullable().optional(),
  createdAt: z.string(),
  ownerId: z.string().uuid().nullable().optional(),
  ownerFullName: z.string().nullable().optional(),
  ownerEmailId: z.string().email().nullable().optional(),
  memberCount: z.number().int().nonnegative(),
  spaceCount: z.number().int().nonnegative(),
  /** Departments configured in this workspace (active + inactive
   *  combined). Surfaced on the Workspaces audit table so the operator
   *  can see at a glance how the workspace is structured. */
  departmentCount: z.number().int().nonnegative().optional().default(0),
  lastActivityAt: z.string().nullable().optional(),
  /** Template key the workspace was seeded from (`'blank'`, `'product_dev'`,
   *  …). Null on legacy rows created before this field shipped, or when
   *  no template was picked. The Workspaces audit table resolves this to a
   *  friendly label using the /workspace-templates catalogue. */
  templateKey: z.string().nullable().optional(),
  /** Workflow this workspace runs on. Null means "fall back to the
   *  tenant's default workflow". Surfaced on the Workflows page so the
   *  Company Admin can flip between workflows per workspace. */
  workflowId: z.string().uuid().nullable().optional(),
  /** `active` (normal) or `inactive` (archived). Defaults to `active`
   *  so the column never renders blank on legacy rows the backend
   *  hadn't tagged before this field shipped. */
  status: z.enum(['active', 'inactive']).optional().default('active'),
})

export const tenantWorkspacesResponse = z.object({
  data: z.array(tenantWorkspaceRow),
})

export const workspaceMember = z.object({
  userId: z.string().uuid(),
  emailId: z.string().email(),
  fullName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  role: z.enum(['admin', 'member', 'guest', 'external']),
  accessLevel: z.enum(['view', 'comment', 'edit', 'full']).nullable().optional(),
  addedAt: z.string().nullable().optional(),
  /** User-panel work role — drives badges in the assignee picker so a
   *  Manager can see who's a Team Leader vs Employee at a glance. */
  workRole: workRole.optional().default('employee'),
  /** Resolved Work Role catalogue name — shown in the people picker so
   *  duplicate display names can be told apart. Null when the user has no
   *  custom Work Role; the UI falls back to the tier label. */
  workRoleName: z.string().nullable().optional(),
  /** Work-Role hierarchy rank (1 = most senior, larger = more junior, null
   *  = unranked). Lets the assignee picker stop a member from assigning a
   *  task UP the hierarchy (to someone more senior than themselves). */
  rank: z.number().int().nullable().optional(),
  /** Department this member belongs to. Surfaced so the Sharing &
   *  Permissions invite list can restrict candidates to the actor's
   *  own department (employees / team leaders); Managers and Company
   *  Admins skip the filter so they can collaborate cross-department. */
  departmentId: z.string().uuid().nullable().optional(),
  /** Department name for display in the people picker. */
  departmentName: z.string().nullable().optional(),
  /** External collaborator flag — true for rows sourced from the grant
   *  closure (reachable-set) rather than workspace membership. The picker
   *  badges these "External"; they carry no department. */
  external: z.boolean().optional().default(false),
})

export const workspaceMembersResponse = z.object({
  data: z.array(workspaceMember),
})

/** Per-Space membership row — narrower than `workspaceMember` because
 *  Space-level access is binary (you're in, or you're not). The
 *  workspace's `accessLevel` still gates what the user can do once
 *  they're inside. */
export const spaceMemberStatus = z.enum(['pending', 'accepted'])

export const spaceMember = z.object({
  userId: z.string().uuid(),
  emailId: z.string().email(),
  fullName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  /** 'pending' until the invitee accepts in their Inbox. The Sharing &
   *  Permissions list shows pending members with a badge so the admin
   *  can see who hasn't joined yet. */
  status: spaceMemberStatus.optional().default('accepted'),
  /** EFFECTIVE access level inside this Space — a per-Space override when
   *  set, otherwise the member's workspace-level access (what they inherit
   *  on a public Space). NULL only if somehow unset. */
  accessLevel: z
    .enum(['view', 'comment', 'edit', 'full'])
    .nullable()
    .optional(),
  /** True when the member has NO explicit SpaceMember row — they have
   *  access only because the Space is public (department gate). The Share
   *  dialog shows these but offers no "remove" (you can't remove someone
   *  from a public Space); setting their access creates an override. */
  inherited: z.boolean().optional().default(false),
  /** Work-role + department tags, surfaced so the dialog can label each
   *  member without a second lookup (the viewer's own roster may not
   *  include every department member). */
  workRole: z.string().nullable().optional(),
  workRoleName: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  // Null for inherited members (no SpaceMember row, so no added-at).
  addedAt: z.string().nullable().optional(),
  /** True for an EXTERNAL collaborator. They hold no WorkspaceMember row —
   *  their reach comes from a Capability grant — so the dialog badges them and
   *  shows their access level read-only (an external's level is derived from
   *  their Work Role, not from the grant). */
  isExternal: z.boolean().optional().default(false),
  /** How the external reaches this Space: a direct `space` grant, or a
   *  `department` grant that happens to cover it. Drives the "via department"
   *  hint. Absent for internal members. */
  grantSource: z.enum(['space', 'department']).nullable().optional(),
  /** True when the grant behind their access is Connection-wide (shared with
   *  the other collaborators from that agency). Removal still only affects
   *  this person — the server records a seat-scoped exclusion rather than
   *  revoking the shared row — but the dialog says so before you click. */
  sharedGrant: z.boolean().optional().default(false),
})

export const spaceMembersResponse = z.object({
  data: z.array(spaceMember),
})

/** Per-List membership row. Shape mirrors `spaceMember` because the
 *  List Share dialog renders the same chip/picker UI as the Space
 *  Share dialog — we just point it at the `/lists/:id/members`
 *  endpoint instead. Reusing the schema shape keeps the validation
 *  logic in one place. */
export const listMember = z.object({
  userId: z.string().uuid(),
  emailId: z.string().email(),
  fullName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  status: spaceMemberStatus.optional().default('pending'),
  accessLevel: z
    .enum(['view', 'comment', 'edit', 'full'])
    .nullable()
    .optional(),
  addedAt: z.string(),
})

export const listMembersResponse = z.object({
  data: z.array(listMember),
})

export const SPACE_VIEW_KEYS = [
  'list',
  'board',
  'calendar',
  'map',
  'activity',
  'team',
  'gantt',
  'mindmap',
  'table',
  'timeline',
  'workload',
]
export const spaceViewKey = z.enum(SPACE_VIEW_KEYS)

/** Union of legacy per-Space categories (`not_started/active/done/closed`)
 *  and the tenant Workflow scheme (`backlog/active/review/done/cancelled`).
 *  The status picker now derives its statuses from the tenant's default
 *  Workflow, so the backend ships workflow categories on the wire; legacy
 *  entries stay listed so any unmigrated Space rows still parse. */
export const TASK_STATUS_CATEGORIES = [
  'backlog',
  'not_started',
  'active',
  'review',
  'done',
  'closed',
  'cancelled',
]
export const taskStatusCategory = z.enum(TASK_STATUS_CATEGORIES)

export const spaceTaskStatus = z.object({
  category: taskStatusCategory,
  name: z.string(),
  color: z.string(),
})

export const DEFAULT_SPACE_TASK_STATUSES = [
  { category: 'not_started', name: 'TO DO', color: '#94a3b8' },
  { category: 'active', name: 'IN PROGRESS', color: '#a855f7' },
  { category: 'closed', name: 'COMPLETE', color: '#10b981' },
]

export const space = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  isPrivate: z.boolean().optional().default(false),
  defaultViews: z.array(spaceViewKey).optional().default(['list', 'board']),
  taskStatuses: z.array(spaceTaskStatus).optional().default(DEFAULT_SPACE_TASK_STATUSES),
  isArchived: z.boolean().optional().default(false),
  /** Personal "My Space" flag + its owner. A personal Space is private to its
   *  owner and shown in the dedicated My Space sidebar section; team views
   *  exclude it. (Declared here or Zod would strip it, turning the
   *  client-side `isPersonal` filters into silent no-ops.)
   *
   *  ONE exception: Space Explorer also lists a COMPANY-WIDE member's personal
   *  Space to a Company Admin / authorised Sub Admin, in its own "Personal
   *  Spaces" section. The server returns those rows only to an authorised
   *  admin and stamps `ownerName` on exactly them — so `ownerName` is the
   *  client's marker for "someone else's personal Space I may view". They open
   *  READ-ONLY (`myAccessLevel: 'view'`) and can never be shared. */
  isPersonal: z.boolean().optional().default(false),
  ownerUserId: z.string().uuid().nullable().optional(),
  /** Owner's display name — present ONLY on another user's personal Space
   *  that this viewer is authorised to see (see `isPersonal` above). */
  ownerName: z.string().nullable().optional(),
  /** Department this Space belongs to. NULL = company-wide, visible to
   *  every workspace member. Non-NULL = only members of that Department
   *  (and platform admins) see it. */
  departmentId: z.string().uuid().nullable().optional(),
  /** Creator of the Space — used by the assignee picker on a private
   *  Space to keep the creator in the candidate set even before they
   *  invite anyone else. */
  createdById: z.string().uuid().nullable().optional(),
  /** Creator's display name, resolved server-side across ALL tenant users
   *  (incl. Company/Sub Admins who have no workspace_members row). Lets the
   *  Space header show "Created by <name>" instead of "Unknown" for an
   *  admin-created Space. Only present on the single-space GET. */
  createdByName: z.string().nullable().optional(),
  /** Creator's email / avatar / work-role / department — powers the Owner row
   *  in the Share dialog so it renders identically for internal and external
   *  viewers (an external viewer can't fetch the workspace roster). */
  createdByEmail: z.string().nullable().optional(),
  createdByAvatarUrl: z.string().nullable().optional(),
  createdByWorkRoleName: z.string().nullable().optional(),
  createdByDepartmentName: z.string().nullable().optional(),
  /** The VIEWER's effective access level for this Space (resolved on
   *  the server: owner/admin → 'full', per-Space override, else the
   *  workspace level). Drives client-side gating of write controls —
   *  'view' is read-only. Only present on the single-space GET. */
  myAccessLevel: z.enum(['view', 'comment', 'edit', 'full']).nullable().optional(),
  /** Whether the VIEWER may manage this Space's membership (add / remove /
   *  change access), resolved server-side. `myManageDepartmentId` is null for a
   *  FULL manager (owner / admin) and set to the viewer's own department for a
   *  DEPARTMENT-SCOPED manager (a cross-department member who may manage only
   *  their own team). Drives the invite / Sharing affordances + the picker. */
  myCanManageMembers: z.boolean().optional(),
  myManageDepartmentId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const spaceListResponse = z.object({
  data: z.array(space),
})

export const listSummary = z.object({
  id: z.string().uuid(),
  spaceId: z.string().uuid(),
  /** Parent folder. null = list lives at the Space root. */
  folderId: z.string().uuid().nullable().optional(),
  name: z.string(),
  isPrivate: z.boolean(),
  /** The List's actual creator. `createdByName` is resolved server-side on the
   *  single-list GET so the Space header can show "Created by <author>" for a
   *  List made by another user (not the Space owner). Absent on the sidebar
   *  list payload, so all three are optional. */
  createdById: z.string().uuid().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  createdByAvatarUrl: z.string().nullable().optional(),
  /** Auto-assignment default (#14) — the user new tasks in this List are
   *  assigned to when no explicit assignee is chosen. null = none. */
  defaultAssigneeId: z.string().uuid().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const listListResponse = z.object({
  data: z.array(listSummary),
})

export const folderSummary = z.object({
  id: z.string().uuid(),
  spaceId: z.string().uuid(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const folderListResponse = z.object({
  data: z.array(folderSummary),
})

export const DOCUMENT_TYPES = ['doc', 'spreadsheet', 'whiteboard']
export const documentType = z.enum(DOCUMENT_TYPES)

export const documentSummary = z.object({
  id: z.string().uuid(),
  spaceId: z.string().uuid(),
  /** Parent folder. null = document lives at the Space root. */
  folderId: z.string().uuid().nullable().optional(),
  type: documentType,
  title: z.string(),
  isArchived: z.boolean().optional().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
})

/** Full document including the editor body (jsonb on the backend). */
export const fullDocument = documentSummary.extend({
  body: z.unknown().nullable().optional(),
})

export const documentListResponse = z.object({
  data: z.array(documentSummary),
})

export const memberRow = z.object({
  id: z.string().uuid(),
  emailId: z.string().email(),
  fullName: z.string(),
  contactNumber: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  role: userRole,
  /** Underlying visibility slug (manager / team_leader / employee) —
   *  drives every existing gate that hasn't been migrated to the new
   *  operations matrix yet. Kept in sync with `workRoleId` via the
   *  server-side resolver. `.catch` so an unexpected legacy value on ONE
   *  member (e.g. a mis-slugged "team leader") degrades to 'employee'
   *  instead of throwing and blanking the ENTIRE Members list. */
  workRole: workRole.catch('employee').optional().default('employee'),
  /** New canonical reference into the tenant's Work Role catalogue.
   *  Null when the user hasn't been assigned a catalogue row (older
   *  tenants from before the catalogue shipped). */
  workRoleId: z.string().uuid().nullable().optional(),
  /** Friendly display name for the Work Role cell — the catalogue
   *  row's name when an FK exists, otherwise the tier slug's label.
   *  The Members table reads this directly so it doesn't have to
   *  maintain its own slug-to-label mapping. */
  workRoleName: z.string().optional(),
  status: userStatus,
  /** Not returned from the invite endpoint — always present on list rows. */
  lastActiveAt: z.string().nullable().optional(),
  createdAt: z.string(),
  /** When offboarded via exit-handover: who their work was handed to (null
   *  otherwise). Drives the re-onboard dialog's "handed over to X" line. */
  offboardedToUserId: z.string().uuid().nullable().optional(),
  offboardedToName: z.string().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  /** Only present when the Members listing was filtered by `workspaceId`.
   *  Surfaces the user's role + access level in that specific workspace
   *  so the audit table can show "admin / full" etc. without a second
   *  request. Undefined when no workspace filter is active. */
  workspaceRole: z.enum(['admin', 'member', 'guest']).optional(),
  workspaceAccessLevel: z.enum(['full', 'edit', 'comment', 'view']).optional(),
  /** Only present when the listing was NOT filtered by `workspaceId` —
   *  the table renders this as a "Workspaces: N" column so admins can
   *  spot users with no workspace memberships at a glance. */
  workspaceCount: z.number().int().nonnegative().optional(),
  /** Per-user workspace memberships (id + name only) — same scope
   *  rule as `workspaceCount` (only set on tenant-wide listings).
   *  The Members table renders these as chips in the Workspaces
   *  column so a Company Admin sees the actual workspace names
   *  instead of a bare count. Empty array for users in no
   *  workspaces. Optional + default so older API responses that
   *  predate the names hydration still parse. */
  workspaces: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .optional()
    .default([]),
  /** Sub Admin delegated operation slugs. The backend surfaces this on
   *  every row (`[]` for non-Sub-Admin members) so the Work Roles page
   *  can render a granted-ops chip cluster without a per-row fetch.
   *  Optional so older API responses that don't emit it still parse. */
  subAdminOperations: z.array(z.string()).optional().default([]),
})

/** Mirrors the Workspace / Members vocabulary so the admin surface
 *  reads consistently. The DB enum was renamed from `'archived'` to
 *  `'inactive'` in a one-time migration; older clients that still
 *  send `'archived'` will hit a 400 from the DTO. */
export const departmentStatus = z.enum(['active', 'inactive'])

export const departmentRow = z.object({
  id: z.string().uuid(),
  /** Departments are workspace-scoped — same name can exist in different
   *  workspaces, so the workspaceId is part of the identity. */
  workspaceId: z.string().uuid().optional(),
  name: z.string(),
  description: z.string().nullable(),
  status: departmentStatus,
  /** Per-Department task workflow. Null = inherits the tenant default
   *  workflow. Set on the Workflows page (Departments & Their Workflows). */
  workflowId: z.string().uuid().nullable().optional(),
  memberCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const departmentList = z.object({
  data: z.array(departmentRow),
})

/** Returned by `GET /departments?name=X` — tenant-wide name lookup
 *  used by the Edit Department dialog's "apply to matching Departments
 *  in other Workspaces" toggle. Each row carries the Workspace name so
 *  the UI can render "Updated in Operations, Supply Chain — failed in
 *  Marketing" without a second round trip. */
export const departmentSiblingRow = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: departmentStatus,
})
export const departmentSiblingList = z.object({
  data: z.array(departmentSiblingRow),
})

// ---------- Work Role Management (Company Admin → /work-roles) -------
//
// Tenant-scoped Work Role catalogue. Promotes TaskMagix's three
// baseline hierarchy levels (Manager / Team Leader / Employee) from
// hard-coded enum values on `User.workRole` into rows the
// Organisation admin can rename / re-describe and add custom Work
// Roles on top of (e.g. "Senior Designer", "QA Lead"). Every Work
// Role anchors to one of the three levels so the existing task-
// visibility cascade keeps working unchanged whether a user carries a
// default or a custom Work Role.
//
// Naming note: the underlying Mongo column is still called `tier`
// (no schema change). The API surface and the frontend speak "level".

export const workRoleLevel = z.enum(['manager', 'team_leader', 'employee'])
/** @deprecated Use `workRoleLevel`. Kept as a back-compat alias for
 *  any consumer still importing the old name. */
export const workRoleTier = workRoleLevel
export const workRoleStatus = z.enum(['active', 'archived'])

export const workRoleRow = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  /** Hierarchy level slug (manager / team_leader / employee). The
   *  API also returns the legacy `tier` field with the same value —
   *  both are accepted on the parse so a deploy mismatch doesn't
   *  break things. */
  level: workRoleLevel,
  /** Friendly label for the level ("Manager Level" / "Team Leader
   *  Level" / "Employee Level"). Sent by the API so the frontend
   *  doesn't have to maintain its own slug-to-label mapping. */
  levelLabel: z.string(),
  /** @deprecated Back-compat alias for `level`. */
  tier: workRoleLevel.optional(),
  /** @deprecated Back-compat alias for `levelLabel`. */
  tierLabel: z.string().optional(),
  status: workRoleStatus,
  /** True for the three seeded baseline rows. They can be renamed
   *  but not deleted, so the visibility model always has an anchor
   *  per level. The UI renders a "Default" pill on these rows and
   *  hides the Delete button. */
  isDefault: z.boolean(),
  /** Flat list of `module.action` operation keys the role grants.
   *  The canonical catalogue is fetched separately from
   *  `/work-roles/operation-catalogue` so the UI knows which keys
   *  exist and how to group them in the checkbox grid. */
  operations: z.array(z.string()),
  /** Hierarchy rank for the top-to-bottom visibility cascade (1 = most
   *  senior; null = unranked → no cascade). Drives the Rank field on the
   *  Add/Edit dialog and the Rank column on the list. */
  rank: z.number().int().nullable().optional(),
  /** Users in the tenant currently carrying this Work Role. Drives
   *  the delete-confirmation copy when admins unassign a custom
   *  role. */
  userCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const workRoleList = z.object({
  data: z.array(workRoleRow),
})

/** [External Collaboration] One external-collaborator seat row on the
 *  Company-Admin management page. Fields kept permissive (z.string(), not
 *  .uuid()) so a backend tweak never silently drops the whole list via a
 *  strict-parse failure — the same defensive shape used by accountEntry. */
export const externalCollaboratorRow = z.object({
  seatId: z.string(),
  userId: z.string().nullable().optional(),
  // System role — 'user' (department-scoped) or 'sub_admin' (company-wide).
  // Permissive string so a future role value is never dropped.
  role: z.string().optional().default('user'),
  fullName: z.string().nullable().optional(),
  emailId: z.string().nullable().optional(),
  orgName: z.string().nullable().optional(),
  kind: z.string().nullable().optional(),
  relationshipLabel: z.string().nullable().optional(),
  status: z.string(),
  memberStatus: z.string().nullable().optional(),
  workRoleName: z.string().nullable().optional(),
  // Work Role id — lets the Edit dialog pre-select the dropdown.
  workRoleId: z.string().nullable().optional(),
  grantCount: z.number().int().nonnegative().optional().default(0),
  // The granted department NAMES — the Department column shows these directly.
  // Empty for legacy space-only grants (client falls back to the count).
  grantDepartmentNames: z.array(z.string()).optional().default([]),
  // The granted department IDS — lets the Edit dialog pre-select the picker.
  grantDepartmentIds: z.array(z.string()).optional().default([]),
  // 'department' (new) | 'space' (legacy) | 'mixed' | null — drives the Access
  // column noun. Permissive string so a future grant type is never dropped.
  grantResourceType: z.string().nullable().optional(),
  // What the collaborator may DO inside those grants: 'view' | 'comment' |
  // 'edit', 'mixed' when their grants disagree, null when they hold none.
  // Permissive string for the same reason as grantResourceType — this schema
  // STRIPS unknown keys, so an undeclared field silently never reaches the
  // table, which is exactly how the Access column first rendered as '—'.
  accessLevel: z.string().nullable().optional(),
  // How many OTHER collaborators a level change would also move, because the
  // grant is owned by the Connection rather than the seat. 0 when nobody else
  // is affected.
  levelSharedWithCount: z.number().int().nonnegative().optional().default(0),
  engagementEndsAt: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
})

export const externalCollaboratorListResponse = z.object({
  data: z.array(externalCollaboratorRow),
})

/** Invite response — the single-use `/join` link the admin shares with the
 *  collaborator (no invite email is sent yet). */
export const inviteExternalCollaboratorResponse = z.object({
  connectionId: z.string(),
  seatId: z.string(),
  memberId: z.string(),
  joinLink: z.string(),
})

/** Per-Work-Role analytics rollup (the Work Role dashboard). Every figure
 *  is a live aggregate keyed by the role members carry. `id` is '' for the
 *  "Unassigned" bucket (members with no Work Role). */
export const workRoleAnalyticsRow = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  memberCount: z.number().int(),
  openTasks: z.number().int(),
  overdueTasks: z.number().int(),
  completedTasks: z.number().int(),
  completionRate: z.number().int(),
  points: z.object({
    total: z.number().int(),
    avg: z.number().int(),
  }),
})
export const workRoleAnalyticsResponse = z.object({
  data: z.array(workRoleAnalyticsRow),
  totals: z.object({
    roleCount: z.number().int(),
    membersAssigned: z.number().int(),
    membersUnassigned: z.number().int(),
    completionRate: z.number().int(),
  }),
})

/** Level catalogue response — the three baseline levels with their
 *  friendly labels. Fed into the Level dropdown on the Add/Edit
 *  dialog. `tier` is kept on each entry as a back-compat alias. */
export const workRoleLevelOption = z.object({
  level: workRoleLevel,
  label: z.string(),
  /** @deprecated Back-compat alias for `level`. */
  tier: workRoleLevel.optional(),
})
export const workRoleLevelOptions = z.object({
  data: z.array(workRoleLevelOption),
})
/** @deprecated Use `workRoleLevelOption` / `workRoleLevelOptions`. */
export const workRoleTierOption = workRoleLevelOption
/** @deprecated Use `workRoleLevelOptions`. */
export const workRoleTierOptions = workRoleLevelOptions

/** Operation catalogue row — one selectable capability the admin can
 *  tick on a Work Role. Grouped by `module` in the Work Role dialog so
 *  the form reads as a tidy matrix (Pages / Task Visibility / Task
 *  Actions / Work Reports / Member Directory / Leaderboard). */
export const operationCatalogueRow = z.object({
  key: z.string(),
  module: z.string(),
  label: z.string(),
})
export const operationCatalogueResponse = z.object({
  data: z.array(operationCatalogueRow),
})

export const paginatedMembers = z.object({
  data: z.array(memberRow),
  pagination: z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    totalCount: z.number().int(),
    totalPages: z.number().int(),
  }),
})

export const inviteUserResponse = z.object({
  user: memberRow,
  // Phase 2 (Join Company): invites no longer mint a temporary password —
  // the backend returns the single-use accept link so the admin can share
  // it out-of-band if the email did not arrive.
  joinUrl: z.string().optional(),
})

// ---------- Bulk member import (CSV/XLSX) ----------
// Preview = dry run: per-row status + a seat plan, no writes.
export const memberImportPreview = z.object({
  fileName: z.string(),
  summary: z.object({
    total: z.number().int(),
    valid: z.number().int(),
    duplicates: z.number().int(),
    errors: z.number().int(),
    seat: z.object({
      limit: z.number().int().nullable(),
      used: z.number().int(),
      remaining: z.number().int().nullable(),
      willImport: z.number().int(),
      willSkipForSeats: z.number().int(),
    }),
  }),
  rows: z.array(
    z.object({
      rowNumber: z.number().int(),
      status: z.string(),
      messages: z.array(z.string()),
      fullName: z.string(),
      email: z.string(),
      role: z.string(),
      workRoleName: z.string(),
      workspaceName: z.string(),
      departmentName: z.string(),
    }),
  ),
})

// Commit = the actual import result, one entry per attempted row.
export const memberImportResult = z.object({
  created: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
  results: z.array(
    z.object({
      rowNumber: z.number().int(),
      email: z.string(),
      fullName: z.string(),
      status: z.string(),
      reason: z.string(),
    }),
  ),
})

// ---------- Platform Admin: SOP integration ----------
export const SOP_AUTH_TYPES = ['none', 'api_key', 'bearer']
export const sopAuthType = z.enum(SOP_AUTH_TYPES)

export const sopConfig = z.object({
  endpointUrl: z.string(),
  // Optional URL template for the per-SOP detail endpoint, e.g.
  // `https://api.example.com/sops/{slug}`. Defaults to '' on older
  // payloads that don't ship the field, so existing tenants keep
  // parsing cleanly.
  detailUrlTemplate: z.string().optional().default(''),
  authType: sopAuthType,
  hasCredential: z.boolean(),
  updatedAt: z.string().nullable(),
})

export const sopTestResult = z.object({
  ok: z.boolean(),
  status: z.number(),
  latencyMs: z.number(),
  message: z.string(),
  bodySnippet: z.string().optional(),
})

// ---------- Platform Admin: Servexa integration (spec A9/A20) ----------
export const SERVEXA_STATUSES = [
  'NOT_CONFIGURED',
  'KEY_GENERATED',
  'PENDING',
  'ACTIVE',
  'REJECTED',
  'REVOKED',
  'ERROR',
]
export const servexaStatus = z.enum(SERVEXA_STATUSES)

/**
 * The Super Admin Integration panel view. Returned verbatim by
 * `GET/PUT /super-admin/servexa` and the connection request/refresh routes.
 *
 * The raw integration key is NEVER in this shape — only `apiKeyMasked` (spec
 * A19). Because `z.object()` strips undeclared keys, parsing here is also the
 * frontend's guarantee that a raw key can never reach the UI even if a future
 * backend change accidentally included one.
 */
export const servexaIntegrationView = z.object({
  hasApiKey: z.boolean(),
  apiKeyMasked: z.string().nullable(),
  apiKeySavedAt: z.string().nullable(),
  baseUrl: z.string().nullable(),
  effectiveBaseUrl: z.string().nullable(),
  baseUrlSource: z.enum(['database', 'environment', 'none']),
  envBaseUrl: z.string().nullable(),
  publicIssueCode: z.string().nullable(),
  // Chat Widget URL (spec A8b value 4, N26) — non-secret, shown in full.
  // Optional so a pre-Chat API still parses.
  chatWidgetUrl: z.string().nullable().optional().default(null),
  hasWebhookSecret: z.boolean(),
  status: servexaStatus,
  connected: z.boolean(),
  lastCheckedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  project: z
    .object({
      name: z.string().nullable(),
      code: z.string().nullable(),
      prefix: z.string().nullable(),
    })
    .nullable(),
  warnings: z.array(z.string()).default([]),
})

// ---------- Servexa: Support Tickets (A6) & Feature Suggestions (A7) ----------
// Exact Servexa contract enums — the UI shows friendly labels but the wire
// values are these. Kept as exported arrays so a form's <option> list and the
// response schema can't drift apart.
export const SERVEXA_TICKET_TYPES = ['BUG', 'SUPPORT', 'INCIDENT', 'QUESTION', 'CHANGE_REQUEST']
export const SERVEXA_TICKET_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
// Local vocabulary: NEW on creation, then only the six folded values a webhook
// can deliver (A13.1). Servexa's richer internal stages never appear.
export const SERVEXA_TICKET_STATUSES = [
  'NEW',
  'ASSIGNED',
  'IN_PROGRESS',
  'PENDING_REVIEW',
  'REWORK',
  'RESOLVED',
  'CLOSED',
]
export const SERVEXA_FEATURE_STATUSES = ['NEW', 'APPROVED', 'REJECTED', 'RELEASED']
const servexaSyncState = z.enum(['pending_sync', 'synced', 'failed'])

export const servexaTicket = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  type: z.enum(SERVEXA_TICKET_TYPES),
  priority: z.enum(SERVEXA_TICKET_PRIORITIES),
  status: z.enum(SERVEXA_TICKET_STATUSES),
  ticketNumber: z.string().nullable(),
  externalReferenceId: z.string(),
  syncState: servexaSyncState,
  lastSyncError: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
})
export const servexaTicketsResponse = z.object({
  data: z.array(servexaTicket),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
})
// The create/retry response is a ticket view PLUS the honest submit outcome.
export const servexaTicketResult = servexaTicket.extend({
  submitted: z.boolean(),
  error: z.string().nullable(),
})

export const servexaFeature = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  businessNeed: z.string().nullable(),
  status: z.enum(SERVEXA_FEATURE_STATUSES),
  featureNumber: z.string().nullable(),
  externalReferenceId: z.string(),
  syncState: servexaSyncState,
  moduleDisabled: z.boolean(),
  lastSyncError: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
})
export const servexaFeaturesResponse = z.object({
  data: z.array(servexaFeature),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
})
export const servexaFeatureResult = servexaFeature.extend({
  submitted: z.boolean(),
  error: z.string().nullable(),
})

// Public, non-secret config for the pre-login "Report an Issue" form (A6a):
// whether it should render at all. The publicIssueCode is NEVER sent here.
export const servexaPreLoginConfig = z.object({ enabled: z.boolean() })
// The pre-login submit response (A6a) — OUR relay's shape
// (servexa.prelogin.service.js), not Servexa's `{ success, data }` envelope:
// the browser never talks to Servexa directly. ticketNumber may be null on the
// silent honeypot no-op — a real client treats any 2xx as success.
export const servexaPreLoginResult = z.object({
  submitted: z.boolean().optional(),
  ticketNumber: z.string().nullable().optional(),
  message: z.string().optional(),
})

// ---------- SOP ----------
export const sopSummary = z.object({
  id: z.string().uuid(),
  listId: z.string().uuid(),
  title: z.string(),
  originalFilename: z.string().nullable().optional(),
  mimeType: z.string().nullable().optional(),
  fileSize: z.number().nullable().optional(),
  createdAt: z.string(),
  // Slice 3a — the SOP's saved Task Blueprint summary (null if none yet).
  blueprint: z
    .object({
      currentVersion: z.number().int().optional().default(1),
      versionCount: z.number().int().optional().default(1),
      taskCount: z.number().int().optional().default(0),
      source: z.string().nullable().optional(),
      updatedAt: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
})

export const sopListResponse = z.object({
  data: z.array(sopSummary),
  // The viewer's SOP permissions for this list, so the UI hides actions they
  // cannot perform (each is also enforced server-side).
  canEdit: z.boolean().optional().default(false),
  canDelete: z.boolean().optional().default(false),
  canCreateTasks: z.boolean().optional().default(false),
})

// Recursive subtask shape. The base type carries a recursive
// `subtasks` field via `z.lazy` so a Task → Subtask → Sub-subtask
// tree (depth 2) coming back from the AI "from Upload" preview
// parses cleanly. The backend cleaner enforces the depth cap; this
// schema validates the SHAPE at any depth.
export const generatedTask = z.lazy(() =>
  z.object({
    name: z.string(),
    description: z.string().optional().default(''),
    // Slice 3c — optional AI-suggested priority + effort estimate.
    priority: z.enum(['urgent', 'high', 'normal', 'low']).nullable().optional(),
    estimatedMinutes: z.number().int().nullable().optional(),
    subtasks: z.array(generatedTask).optional().default([]),
  }),
)

export const generateSopResponse = z.object({
  sopId: z.string().uuid(),
  sopTitle: z.string(),
  tasks: z.array(generatedTask),
  provenance: z
    .object({
      provider: z.string(),
      // Nullable: a Task Blueprint cache hit may carry no model.
      model: z.string().nullable().optional(),
      // 'cache' = reused from a saved Task Blueprint (NO AI call, no
      // credits); 'fresh' = just generated. Optional so older API
      // responses (which omit it) still parse.
      source: z.string().optional(),
      blueprintId: z.string().optional(),
      generatedAt: z.string().nullable().optional(),
      // Slice 3b — true when the SOP text changed since this blueprint was
      // generated (reused as-is; the UI offers Regenerate).
      outdated: z.boolean().optional(),
    })
    .optional(),
})

// ── Task Blueprint (Slice 2) — saved, versioned breakdown of an SOP ──
export const sopBlueprintVersion = z.object({
  version: z.number().int(),
  source: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  taskCount: z.number().int().optional().default(0),
})
export const sopBlueprint = z.object({
  id: z.string(),
  sopId: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  currentVersion: z.number().int().optional().default(1),
  tasks: z.array(generatedTask),
  versions: z.array(sopBlueprintVersion).optional().default([]),
  updatedAt: z.string().nullable().optional(),
  // Slice 3b — true when the SOP content changed since the current version.
  isOutdated: z.boolean().optional(),
})
export const sopBlueprintResponse = z.object({
  blueprint: sopBlueprint.nullable(),
})

// The saved blueprints reusable from a list (the "From Blueprint" create-task
// method). Carries the full task tree so the picker can open the review
// preview without a second fetch.
export const blueprintListItem = z.object({
  id: z.string(),
  title: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  origin: z.string().optional(), // 'upload' | 'library'
  taskCount: z.number().int().optional().default(0),
  currentVersion: z.number().int().optional().default(1),
  versionCount: z.number().int().optional().default(1),
  lastUsedAt: z.string().nullable().optional(),
  usageCount: z.number().int().optional().default(0),
  updatedAt: z.string().nullable().optional(),
  tasks: z.array(generatedTask).optional().default([]),
})
export const blueprintsListResponse = z.object({
  data: z.array(blueprintListItem),
})

export const approveSopResponse = z.object({
  message: z.string(),
  taskIds: z.array(z.string().uuid()),
})

// ---------- Tasks ----------
export const TASK_PRIORITIES = ['urgent', 'high', 'normal', 'low']
export const taskPriority = z.enum(TASK_PRIORITIES)

export const task = z.object({
  id: z.string().uuid(),
  listId: z.string().uuid(),
  /** Parent task id for subtasks (null = top-level). One level of nesting only. */
  parentTaskId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  /** Human-readable status name from the parent Space's taskStatuses jsonb. */
  status: z.string(),
  priority: taskPriority.nullable().optional(),
  /** Manual drag-and-drop ordering key. MUST be declared here: the List
   *  view sorts rows by `sortOrder`, and Zod strips any response field
   *  NOT in this schema. Omitting it silently broke drag-to-reorder —
   *  the backend persisted the new order, the refetch returned it, but
   *  the parsed tasks had no `sortOrder`, so every row compared equal
   *  and the list never repositioned (the row snapped back on drop). */
  sortOrder: z.number().int().optional(),
  /** Optional custom bar colour for the Gantt view. Stored as a hex
   *  string (e.g. "#1E5631"). Null = fall back to priority colour,
   *  then to the Space's configured status colour. */
  color: z.string().nullable().optional(),
  /** All users assigned to this task. Order is insertion order. */
  assigneeIds: z.array(z.string().uuid()).default([]),
  /** Per-assignee display info (name + avatar), resolved server-side so the
   *  Assignee column can always draw a named avatar — even for an assignee
   *  outside the Space's scoped assignable roster (who would otherwise render
   *  as an anonymous "+1"). `fullName` is null only for a deleted user. */
  assignees: z
    .array(
      z.object({
        userId: z.string(),
        fullName: z.string().nullable().optional(),
        avatarUrl: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  /** ISO 8601 date-time, nullable. */
  startDate: z.string().nullable().optional(),
  /** ISO 8601 date-time, nullable. */
  dueDate: z.string().nullable().optional(),
  /** Whether the user explicitly set a time of day on startDate /
   *  dueDate. Lets the Due time / Start time columns show an explicit
   *  "12:00 AM" — otherwise indistinguishable from a date-only task,
   *  which is also stored at midnight. */
  startDateHasTime: z.boolean().optional(),
  dueDateHasTime: z.boolean().optional(),
  /** True when the due time was auto-assigned to 23:59 (end of day)
   *  because a date was set with no explicit time. Drives the "End of
   *  day" label instead of exposing "11:59 PM". */
  dueEndOfDay: z.boolean().optional(),
  /** ISO 8601 date-time, set when the task entered a closed-category status. */
  completedAt: z.string().nullable().optional(),
  /** Per-tenant custom field values keyed by def key (e.g. a "Task
   *  Type" select) — see Configuration → Custom Fields. Optional +
   *  defaulted so older API payloads still parse. */
  customFields: z.record(z.string(), z.any()).optional().default({}),
  /** [Accountability] Named checker/verifier (a specific user), the expected
   *  output, and whether proof is required before the task can be completed.
   *  All optional + defaulted so existing API payloads parse unchanged. */
  verifierId: z.string().uuid().nullable().optional(),
  expectedOutput: z.string().nullable().optional(),
  proofRequired: z.boolean().optional().default(false),
  /** [Accountability · Phase 2] Verification workflow state. Without these,
   *  the schema STRIPS them and the task-detail verification controls
   *  (Submit / Approve / Reject, checker note) never render — the chip falls
   *  back to "Not submitted" via `?? 'none'` but the strict `=== 'none'`
   *  button checks fail. Kept as loose strings to avoid enum coupling. */
  approvalStatus: z.string().nullable().optional(),
  approvalNote: z.string().nullable().optional(),
  approvalRequestedAt: z.string().nullable().optional(),
  /** [Recurring] Template task of the series a generated occurrence belongs to.
   *  Lets the Repeat cell open the series' pause/stop dialog from the current
   *  occurrence (not just the original template). Null for templates + non-
   *  recurring tasks. */
  recurringSourceId: z.string().uuid().nullable().optional(),
  /** Task creator — surfaced so the List view can show a "Created by"
   *  column. `createdByName` / `createdByAvatarUrl` are resolved
   *  server-side; null when the creator can't be resolved (deleted). */
  createdById: z.string().uuid().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  createdByAvatarUrl: z.string().nullable().optional(),
  /** Aggregated counts for the list's Comments / Attachments cells, resolved
   *  server-side so those cells render the number WITHOUT each firing its own
   *  request (the old per-row fetch flickered the Space on open). The popover
   *  still fetches the full thread / file list when opened. */
  commentCount: z.number().int().nonnegative().optional().default(0),
  attachmentCount: z.number().int().nonnegative().optional().default(0),
  /** Recurrence state for the list's "Repeat" column, resolved server-side:
   *  `'active'` (an active recurring rule generates occurrences), `'paused'`
   *  (a rule exists but is paused), or null / absent (not recurring). Only
   *  top-level tasks can carry a rule; subtasks are always null. Kept a plain
   *  string (not an enum) so a future state can't break the parse. */
  recurring: z.string().nullable().optional(),
  /** The series' schedule, resolved server-side the same way as `recurring`
   *  above (own rule, else the template's via `recurringSourceId`). Feeds the
   *  Repeat column's day summary so the schedule is readable without opening
   *  the Repeat dialog. `passthrough` because the schedule shape is owned by
   *  the recurrence engine and gains fields (byMonthDay, cron, …) over time —
   *  a new one must never fail the task-list parse. */
  recurringSchedule: z
    .object({
      freq: z.string().optional(),
      interval: z.number().optional(),
      byWeekday: z.array(z.number()).optional(),
      byMonthDay: z.number().optional(),
      cron: z.string().optional(),
      timezone: z.string().nullable().optional(),
    })
    .passthrough()
    .nullable()
    .optional(),
  /** Per-task edit capability flags, computed server-side from the
   *  Work-Role permission matrix + Space access level. The UI disables the
   *  matching controls when false; the server still enforces the gate on
   *  write. `canEditStatus` → status dropdown / board drag; `canEditPlanning`
   *  → aggregate (true if ANY planning field is editable). The per-field
   *  flags (`canEditStartDate` / `canEditDueDate` / `canEditPriority` /
   *  `canEditAssignee`) drive each control individually — they also encode
   *  the per-field rank lock (a junior can't override a senior's value).
   *  Absent (undefined) means "not provided by this source" — treated as
   *  editable by the UI. */
  canEditStatus: z.boolean().optional(),
  canEditPlanning: z.boolean().optional(),
  canEditStartDate: z.boolean().optional(),
  canEditDueDate: z.boolean().optional(),
  canEditPriority: z.boolean().optional(),
  canEditAssignee: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const taskListResponse = z.object({
  data: z.array(task),
})

/** A recurring-task rule (the "Repeat" config attached to a template task).
 *  The backend schedule-generates one occurrence per `nextRunAt`. */
export const recurringRuleSchedule = z.object({
  freq: z.enum(['daily', 'weekly', 'monthly', 'cron']),
  interval: z.number().int().min(1).optional(),
  byWeekday: z.array(z.number().int().min(0).max(6)).optional(),
  byMonthDay: z.number().int().min(1).max(31).optional(),
  cron: z.string().optional(),
})
export const recurringRule = z.object({
  id: z.string(),
  sourceTaskId: z.string(),
  schedule: recurringRuleSchedule,
  timezone: z.string().nullable().optional(),
  nextRunAt: z.string().nullable().optional(),
  lastRunAt: z.string().nullable().optional(),
  isActive: z.boolean(),
  endDate: z.string().nullable().optional(),
  maxOccurrences: z.number().int().nullable().optional(),
  instancesCreated: z.number().int().optional().default(0),
})
export const recurringRulesResponse = z.object({
  data: z.array(recurringRule),
})

/** A team-task row carries Space + List context so the Team Tasks page can
 *  show where each task lives without an extra fetch. */
export const teamTask = task.extend({
  listName: z.string(),
  spaceId: z.string().uuid(),
  spaceName: z.string(),
  /** Project the task's List belongs to, when it has one. Nullable — a List may
   *  sit directly under the Space with no project. Drives the
   *  Space / Project / List location breadcrumb on the Team Tasks page. */
  projectName: z.string().nullable().optional(),
  /** Workspace the task lives in — needed to load member candidates for
   *  the assignee picker on the Team Tasks Add Task dialog. */
  workspaceId: z.string().uuid(),
  createdById: z.string().uuid(),
  /** Aggregated comment count — drives the Comments column on the
   *  Team Tasks list. Falls back to 0 when the column is empty. */
  commentCount: z.number().int().nonnegative().optional().default(0),
  /** Status colour resolved from THIS task's own workflow (its Space's
   *  Department workflow, else the tenant default). Null when the workflow
   *  has no colour for the status — the client then applies its fallback. */
  statusColor: z.string().nullable().optional(),
  /** How the task was created (manual / blueprint / ai_sop / ai_upload /
   *  recurring). Carried so a Team-Analytics drill-down can filter the board
   *  by source. Loose string + optional so older payloads still parse.
   *  (approvalStatus is already inherited from the base `task` schema.) */
  creationSource: z.string().nullable().optional(),
})

export const teamTaskResponse = z.object({
  workRole: workRole,
  departmentName: z.string().nullable(),
  data: z.array(teamTask),
  /** Tenant default-workflow states — the SAME list the dashboard's
   *  "Tasks by Status" donut pads its zero-count legend entries from.
   *  The Team Tasks page unions these into its status columns so a
   *  configured-but-empty status (e.g. IN PROGRESS with 0 tasks) shows
   *  on both surfaces instead of only in the donut (QA-reported).
   *  Optional so older API payloads still parse. */
  workflowStates: z
    .array(
      z.object({
        label: z.string(),
        color: z.string().nullable().optional(),
      }),
    )
    .optional(),
})

/* ── Team Task Analytics + Employee Day-Audit (Supervision) ──────────────────
 * Mirrors the shapes returned by team-analytics.service.js. */
/** Per-employee counts shared by the matrix rows AND the Unassigned row.
 *  `completionRate` / `overdueRate` are integer percentages, null on a zero
 *  denominator (the UI renders "—"). */
const analyticsCounts = {
  toDo: z.number().int(),
  inProgress: z.number().int(),
  complete: z.number().int(),
  overdue: z.number().int(),
  verificationPending: z.number().int(),
  createdViaSop: z.number().int(),
  createdViaAi: z.number().int(),
  total: z.number().int(),
  completionRate: z.number().int().nullable(),
  overdueRate: z.number().int().nullable(),
}
const teamAnalyticsRow = z.object({
  userId: z.string(),
  employee: z.string(),
  email: z.string(),
  workRoleId: z.string().nullable(),
  workRole: z.string().nullable(),
  departmentId: z.string().nullable(),
  department: z.string().nullable(),
  ...analyticsCounts,
})
const analyticsBucket = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number().int(),
})
const analyticsOption = z.object({ id: z.string(), name: z.string() })
export const teamAnalyticsResponse = z.object({
  data: z.array(teamAnalyticsRow),
  /** Distinct-task KPIs for the whole scope (a shared task counts once), plus
   *  the accountability axis (verification*) and the source split. Reconciles
   *  with the export row count for identical filters. */
  kpis: z.object({
    total: z.number().int(),
    toDo: z.number().int(),
    inProgress: z.number().int(),
    complete: z.number().int(),
    overdue: z.number().int(),
    unassigned: z.number().int(),
    verificationPending: z.number().int(),
    verificationApproved: z.number().int(),
    verificationRejected: z.number().int(),
    createdViaSop: z.number().int(),
    createdViaAi: z.number().int(),
    employees: z.number().int(),
    completionRate: z.number().int().nullable(),
    overdueRate: z.number().int().nullable(),
  }),
  /** Work in scope assigned to nobody on the team (accountability gap). */
  unassigned: z.object(analyticsCounts),
  statusBreakdown: z.array(analyticsBucket),
  sourceBreakdown: z.array(analyticsBucket),
  /** Server-supplied Space + List options for the scope-narrowing filters. */
  filterOptions: z
    .object({
      spaces: z.array(analyticsOption).default([]),
      lists: z
        .array(analyticsOption.extend({ spaceId: z.string() }))
        .default([]),
    })
    .optional()
    .default({ spaces: [], lists: [] }),
  scopeMode: z.enum(['all', 'department', 'self']),
})

const auditEvent = z.object({
  at: z.string(),
  kind: z.string(),
  taskId: z.string().nullable(),
  taskName: z.string().nullable(),
  detail: z.string(),
})
export const employeeDayAuditResponse = z.object({
  employee: z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
  }),
  date: z.string(),
  summary: z.object({
    tasksCreated: z.number().int(),
    tasksCompleted: z.number().int(),
    comments: z.number().int(),
    reportsSubmitted: z.number().int(),
    pointsEarned: z.number().int(),
  }),
  timeline: z.array(auditEvent),
})

/** Single team task for the read-only details popup on Team Tasks. The task
 *  is the same shape as a board row, enriched server-side with the heavier
 *  detail fields the list omits (description, custom fields, named assignees /
 *  creator). */
export const teamTaskDetailResponse = z.object({
  task: teamTask,
  workflowStates: z
    .array(
      z.object({
        label: z.string(),
        color: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
})

// ---------- Reports (Section 13) ----------

export const reportType = z.enum(['daily', 'weekly', 'monthly'])

/** §1 Work details row. `taskId` non-null = pulled from a TaskMagix task at
 *  submit time (project / module / status / etc. auto-derived as snapshot).
 *  `taskId === null` = "Manual entry" for off-system work. */
export const workItem = z.object({
  taskId: z.string().uuid().nullable().default(null),
  title: z.string().min(1).max(300),
  description: z.string().max(2000).optional().default(''),
  project: z.string().max(200).optional().default(''),
  module: z.string().max(200).optional().default(''),
  // Free-form, NOT an enum: Task Types are an admin/Manager-configured,
  // now per-department catalogue, so a custom value (e.g. "Review",
  // "Regression Testing") must parse — a z.enum here 422s every custom
  // type on submit (and drops saved reports on read). Mirrors the backend
  // reports DTO (z.string().trim().max(50)).
  type: z.string().trim().max(50).default(''),
  // Free-form, NOT an enum — Item Statuses are a per-department configurable
  // catalogue (Report Settings), same as `type` above. A z.enum here 422s
  // custom statuses on submit and drops saved reports on read. Mirrors the
  // backend reports DTO (z.string().trim().max(50)).
  status: z.string().trim().max(50).default('in_progress'),
  priority: z.enum(['low', 'medium', 'high']).default('medium'),
  // Ceiling is the whole-period max (24h × 31d), matching the backend reports
  // DTO. A weekly/monthly rollup sums the same task across many dailies into
  // one row, so its total can exceed 24h — a `.max(24)` here threw a ZodError
  // that blanked the whole AI draft / saved-report read. A single DAY stays
  // capped at 12h server-side.
  timeSpent: z.number().min(0).max(24 * 31).default(0),
})

export const blockerItem = z.object({
  blocker: z.string().min(1).max(500),
  dependencyOn: z.string().max(200).optional().default(''),
  resolutionStatus: z
    .enum(['open', 'awaiting', 'in_review', 'resolved'])
    .default('open'),
})

export const meetingItem = z.object({
  title: z.string().min(1).max(200),
  purpose: z.string().max(500).optional().default(''),
  // No `.int()` — the backend reports DTO accepts non-integer minutes, so
  // the READ schema must too, or reading a saved report with a fractional
  // duration throws a ZodError and blanks the whole Work Reports page.
  durationMinutes: z.number().min(0).max(24 * 60).default(0),
  attendees: z.string().max(200).optional().default(''),
  /** YYYY-MM-DD of the daily the meeting came from — stamped by the
   *  weekly/monthly rollup so period views can show a Date column.
   *  Empty for meetings entered directly on a daily. */
  date: z.string().max(10).optional().default(''),
})

/** Old reports stored `blockers: string[]`; new reports store structured
 *  rows. Accept both on read so historical submissions render without
 *  crashing the form. */
const blockersArrayCompat = z
  .array(z.union([z.string(), blockerItem]))
  .default([])
  .transform((arr) =>
    arr.map((b) =>
      typeof b === 'string'
        ? { blocker: b, dependencyOn: '', resolutionStatus: 'open' }
        : b,
    ),
  )

export const reportContent = z.object({
  summary: z.string().optional().default(''),
  workItems: z.array(workItem).default([]),
  accomplishments: z.array(z.string()).default([]),
  blockers: blockersArrayCompat,
  plansForNext: z.array(z.string()).default([]),
  meetings: z.array(meetingItem).default([]),
  notes: z.string().optional().default(''),
  hoursLogged: z.number().optional(),
  taskIds: z.array(z.string().uuid()).default([]),
})

export const workReport = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  /** Joined from users on the list endpoint — undefined on the submit
   *  endpoint, which only returns the report row. */
  userFullName: z.string().optional(),
  userEmailId: z.string().email().optional(),
  /** The report author's department — joined on the list endpoint so the
   *  Company Admin can view / filter Work Reports department-wise. Null
   *  when the author has no department. */
  departmentId: z.string().uuid().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  type: reportType,
  periodStart: z.string(),
  periodEnd: z.string(),
  content: reportContent,
  aiDrafted: z.boolean(),
  submittedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const workReportsResponse = z.object({
  data: z.array(workReport),
})

// ── Unified Goals (Weekly + Monthly) ─────────────────────────────────────────
// One row per goal. `progress`/`health`/`score`/`completed` are all server-
// DERIVED (from key results + linked tasks + sub-goals); the client only reads
// them.

/** A measurable key result on a goal. `progress` is the server-computed 0–100
 *  for its bar; the value fields drive the editor. */
export const goalKeyResult = z.object({
  id: z.string().optional(),
  type: z.enum(['number', 'currency', 'percentage']),
  name: z.string().nullable().optional(),
  start: z.number().nullable().optional(),
  target: z.number().nullable().optional(),
  current: z.number().nullable().optional(),
  progress: z.number().optional(),
})

/** A single unified goal, as returned by `/goals`. Every field the server
 *  sends must be declared (Zod strips undeclared keys). */
export const goal = z.object({
  id: z.string(),
  tenantId: z.string().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  type: z.enum(['weekly', 'monthly']),
  priority: z.enum(['urgent', 'high', 'normal', 'low']).optional().default('normal'),
  startDate: z.string(),
  targetDate: z.string(),
  ownerId: z.string(),
  ownerName: z.string().nullable().optional(),
  createdById: z.string().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  followerIds: z.array(z.string()).optional().default([]),
  teamIds: z.array(z.string()).optional().default([]),
  parentGoalId: z.string().nullable().optional(),
  tags: z.array(z.string()).optional().default([]),
  keyResults: z.array(goalKeyResult).optional().default([]),
  linkedTaskIds: z.array(z.string()).optional().default([]),
  linkedSpaceIds: z.array(z.string()).optional().default([]),
  manualStatus: z
    .enum(['not_started', 'in_progress', 'completed', 'on_hold', 'carried_forward'])
    .optional()
    .default('not_started'),
  /** Rolled-up 0–100; null ⇒ nothing measurable (manual status governs). */
  progress: z.number().nullable().optional(),
  /** Derived health — kept a plain string so a new value can't break parse. */
  health: z.string().optional(),
  score: z.number().optional(),
  completed: z.boolean().optional().default(false),
  linkedProgress: z
    .object({ done: z.number(), total: z.number() })
    .nullable()
    .optional(),
  childCount: z.number().optional().default(0),
  updatedAt: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
})

/** `/goals` list response for a period + type. */
export const goalsResponse = z.object({
  type: z.enum(['weekly', 'monthly']),
  periodStart: z.string(),
  periodEnd: z.string(),
  // True when the list reflects a custom [from, to] range (not the period).
  custom: z.boolean().optional().default(false),
  goals: z.array(goal).default([]),
  progress: z.object({
    completed: z.number().default(0),
    total: z.number().default(0),
  }),
})

/** A person reference (owner / follower / team) resolved server-side. */
const goalPersonRef = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  // Present for follower people (drives the profile-avatar display); absent/null
  // for team (department) refs, which render as name chips.
  avatarUrl: z.string().nullable().optional(),
})

/** A progress update on a goal (the monthly cadence log). */
export const goalUpdate = z.object({
  id: z.string(),
  at: z.string(),
  byId: z.string().nullable().optional(),
  byName: z.string().nullable().optional(),
  note: z.string(),
  progress: z.number().nullable().optional(),
  /** Distinct viewers of this progress update (excludes the author). */
  viewCount: z.number().optional().default(0),
  /** Identities behind viewCount, so the eye control can list who viewed. */
  viewers: z.array(goalPersonRef).optional().default([]),
})

/** `/goals/assignable-departments` — the departments the actor may assign into
 *  (step 1 of the assign flow). */
export const goalAssignableDepartmentsResponse = z.object({
  data: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
})

/** `/goals/assignable-owners` — the members the actor may assign a goal to,
 *  with their department (company-wide actors see people across departments). */
export const goalAssignableOwnersResponse = z.object({
  data: z
    .array(
      z.object({
        id: z.string(),
        fullName: z.string(),
        emailId: z.string().nullable().optional(),
        // Work Role (designation) + department shown under the name, matching
        // the Space assignee picker. `workRole` is the enum tier fallback.
        workRole: workRole.optional().default('employee'),
        workRoleName: z.string().nullable().optional(),
        departmentName: z.string().nullable().optional(),
      }),
    )
    .default([]),
})

/** `/goals/:id` detail response — the goal plus its direct children, resolved
 *  people/team names and the check-in log. */
export const goalDetail = goal.extend({
  children: z.array(goal).optional().default([]),
  ownerName: z.string().nullable().optional(),
  // Owner avatar so the People section renders the owner as a profile avatar
  // (matching followers + the assignee stack), not a bare name.
  ownerAvatarUrl: z.string().nullable().optional(),
  followers: z.array(goalPersonRef).optional().default([]),
  teams: z.array(goalPersonRef).optional().default([]),
  updates: z.array(goalUpdate).optional().default([]),
  // Server-computed permission flags: whether the viewer may manage (edit /
  // delete / manage followers / status / dates) or reassign the owner. Drive the
  // UI controls; authorisation is always re-enforced server-side.
  canManage: z.boolean().optional().default(false),
  canReassign: z.boolean().optional().default(false),
  // Short-lived correction path: the member this goal was JUST reassigned away
  // from may take ownership back (revert a mistaken hand-off) within a grace
  // window, even without creator/supervisor rights. Server re-enforces the window.
  canRevertReassign: z.boolean().optional().default(false),
})

/** Deterministic + AI-narrated analysis for a period of unified goals. Facts
 *  are deterministic; `summary`/`insights` etc. fill in on the AI generate path.
 *  `provenance` is 'rule-based' until an AI narrative is generated. */
export const goalsAnalysisResponse = z.object({
  type: z.enum(['weekly', 'monthly']),
  periodStart: z.string(),
  periodEnd: z.string(),
  progress: z.enum(['on_track', 'partial', 'attention']).nullable().optional(),
  /** Average rolled-up progress (0–100) across the period's goals. */
  averageProgress: z.number().optional().default(0),
  summary: z.string().nullable().optional(),
  goalSummary: z
    .object({
      total: z.number().default(0),
      completed: z.number().default(0),
      inProgress: z.number().default(0),
      notStarted: z.number().default(0),
      onHold: z.number().default(0),
      carriedForward: z.number().default(0),
    })
    .default({}),
  taskSummary: z
    .object({
      hoursLogged: z.number().default(0),
      tasksCompleted: z.number().default(0),
      tasksTouched: z.number().default(0),
      meetings: z.number().default(0),
      daysReported: z.number().default(0),
    })
    .default({}),
  achievements: z.array(z.string()).default([]),
  pending: z.array(z.object({ name: z.string(), status: z.string() })).default([]),
  blockers: z
    .array(
      z.object({
        blocker: z.string(),
        dependencyOn: z.string().nullable().optional(),
        resolutionStatus: z.string().nullable().optional(),
      }),
    )
    .default([]),
  insights: z.array(z.string()).default([]),
  recommendations: z.array(z.string()).default([]),
  suggestedNextGoals: z.array(z.string()).default([]),
  provenance: z.string().optional(),
})

/** Deterministic numbers computed server-side from the raw dailies.
 *  Surfaced read-only above the editable report so the user (and their
 *  manager) sees the period at a glance without trusting the AI to add
 *  numbers up. Optional because the backend may omit it on legacy
 *  responses or empty-period stubs. */
export const reportKeyMetrics = z.object({
  hoursLogged: z.number().min(0).default(0),
  tasksCompleted: z.number().min(0).default(0),
  tasksTouched: z.number().min(0).default(0),
  meetingsAttended: z.number().min(0).default(0),
  meetingMinutes: z.number().min(0).default(0),
  daysReported: z.number().min(0).default(0),
  topProjects: z
    .array(z.object({ name: z.string(), hours: z.number().min(0) }))
    .default([]),
  topModules: z
    .array(z.object({ name: z.string(), hours: z.number().min(0) }))
    .default([]),
  hoursByType: z
    .array(z.object({ name: z.string(), hours: z.number().min(0) }))
    .default([]),
})

export const reportRollup = z.object({
  type: reportType,
  periodStart: z.string(),
  periodEnd: z.string(),
  aiDrafted: z.boolean(),
  content: reportContent,
  /** Present on weekly + monthly drafts. Computed deterministically from
   *  the dailies, so the numbers can't drift even if the AI rephrases
   *  the narrative. */
  keyMetrics: reportKeyMetrics.optional(),
  provenance: z
    .object({
      provider: z.string(),
      model: z.string().nullable().optional(),
    })
    .optional(),
})

/** Report Recipient — a person who receives the tenant's admin-facing
 *  report emails, embedded on the schedule row. `designation` is free-text
 *  (identification only). Delivery is keyed on email + `status === 'active'`. */
export const reportRecipient = z.object({
  id: z.string(),
  fullName: z.string(),
  email: z.string(),
  designation: z.string().optional().default(''),
  status: z.enum(['active', 'inactive']),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
})

export const reportSchedule = z.object({
  tenantId: z.string().uuid(),
  dailyEnabled: z.boolean(),
  dailyReminderAt: z.string(),
  /** Weekday indexes (0 = Sunday … 6 = Saturday) on which the daily
   *  reminder fires. Defaults to Mon–Fri server-side. */
  // The ReportSchedule model documents "Empty array = never fire", so a
  // stored row can legitimately carry `[]`. This schema parses GET
  // responses (the dashboard overview + the Report Schedule page), not
  // the update form, so it must accept every value the model allows —
  // a `.min(1)` here rejected a valid empty row and threw a ParseError
  // that blanked the whole dashboard. Form-side "pick at least one day"
  // validation lives on the write DTO, where it belongs.
  dailyActiveDays: z
    .array(z.number().int().min(0).max(6))
    .min(0)
    .max(7)
    .default([1, 2, 3, 4, 5]),
  weeklyEnabled: z.boolean(),
  weeklyDayOfWeek: z.number().int().min(0).max(6),
  weeklyReminderAt: z.string(),
  monthlyEnabled: z.boolean(),
  // 1..28 = specific day; 31 = "Last day of month" sentinel (clamped to
  // the month's length by the reminder runner + deadline calc).
  monthlyDayOfMonth: z.number().int().min(1).max(31),
  monthlyReminderAt: z.string(),
  /** Which calendar month the monthly reminder describes. `previous`
   *  = the just-closed month ("submit last month's report"); `current`
   *  = the month containing the trigger day ("wrap up this month").
   *  Defaults to `previous` on older payloads that don't ship it. */
  // QA ruling 2026-07-22: `current` is the default — the Monthly Report
  // page must open on the month in progress, not the just-closed one.
  monthlyScope: z.enum(['previous', 'current']).optional().default('current'),
  /** Missed-report escalations. Optional + defaulted so older payloads
   *  parse cleanly without a backend migration before the UI ships. */
  dailyGraceEnabled: z.boolean().optional().default(true),
  weeklyAdminDigestEnabled: z.boolean().optional().default(true),
  monthlyAdminDigestEnabled: z.boolean().optional().default(true),
  /** Daily Task Assignment Digest — the "who's working on what" roster to
   *  Company Admins + supervisors. Optional + defaulted (OFF, noon) so older
   *  payloads and rows predating the fields parse cleanly. */
  taskDigestEnabled: z.boolean().optional().default(false),
  taskDigestReminderAt: z.string().optional().default('12:00'),
  /** Effective daily send times for the assignment digest. The API always
   *  returns at least one (legacy rows fall back to [taskDigestReminderAt]). */
  taskDigestReminderTimes: z.array(z.string()).optional().default([]),
  /** Explicit To/CC/BCC digest recipients the Company Admin manages. */
  taskDigestRecipients: z
    .array(
      z.object({
        id: z.string().optional(),
        fullName: z.string(),
        email: z.string(),
        kind: z.enum(['to', 'cc', 'bcc']).optional().default('to'),
        status: z.enum(['active', 'inactive']).optional().default('active'),
        createdAt: z.string().nullable().optional(),
        updatedAt: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  /** Weekly Repeat Task Report — a roster of recurring tasks + health, sent
   *  weekly. Separate from the daily task digest. Optional + defaulted so
   *  older payloads (and rows predating the fields) parse. */
  repeatReportEnabled: z.boolean().optional().default(false),
  repeatReportDayOfWeek: z.number().int().min(0).max(6).optional().default(1),
  repeatReportReminderAt: z.string().optional().default('12:00'),
  /** Repeat Task Creation Time — the daily wall-clock (company timezone) at
   *  which recurring/repeat tasks are generated. Optional + defaulted to local
   *  midnight so older payloads and rows predating the field parse cleanly. */
  repeatTaskCreationAt: z.string().optional().default('00:00'),
  /** Default Task Notification Time — the fallback wall-clock (company timezone)
   *  at which a task's ASSIGNMENT email is sent when the task has a start date
   *  but no specific start time. Separate from the creation time above. Optional
   *  + defaulted to 09:00 so older payloads and pre-field rows parse cleanly. */
  defaultTaskNotificationAt: z.string().optional().default('09:00'),
  repeatReportRecipients: z
    .array(
      z.object({
        id: z.string().optional(),
        fullName: z.string(),
        email: z.string(),
        kind: z.enum(['to', 'cc', 'bcc']).optional().default('to'),
        status: z.enum(['active', 'inactive']).optional().default('active'),
        createdAt: z.string().nullable().optional(),
        updatedAt: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  /** Report Recipients — embedded on the schedule row. Optional +
   *  defaulted so older payloads (and rows predating the field) parse. */
  reportRecipients: z.array(reportRecipient).optional().default([]),
  updatedById: z.string().uuid().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
})

// `myTask` + `workspaceOverviewResponse` were originally declared here
// but they reference `approvalStatus` which is defined further down the
// file. Moved them to after `approvalStatus` to avoid a temporal dead
// zone error that crashed the whole app at module-load time. Keep this
// section as a marker so future searches still find the User-panel
// personal endpoints in their natural order.

// ---------- Company Admin: API keys ----------

export const apiKey = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  scopes: z.array(z.string()).default(['read']),
  rateLimitPerHour: z.number().int(),
  lastUsedAt: z.string().nullable().optional(),
  totalRequests: z.number().int(),
  isActive: z.boolean(),
  expiresAt: z.string().nullable().optional(),
  createdAt: z.string(),
  createdById: z.string().uuid().nullable().optional(),
  createdByName: z.string().nullable().optional(),
})
export const apiKeysResponse = z.object({ data: z.array(apiKey) })

// ---------- Platform Admin: Announcements ----------

/** A published platform announcement (Super Admin → all tenants). */
export const announcement = z.object({
  id: z.string().uuid(),
  title: z.string(),
  body: z.string(),
  audience: z.enum(['all_users', 'company_admins']),
  priority: z.enum(['normal', 'high']),
  recipientCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  createdById: z.string().uuid().nullable().optional(),
  createdByName: z.string().nullable().optional(),
})
export const announcementListResponse = z.object({
  data: z.array(announcement),
})
/** POST response = the created row, including the realised reach. */
export const announcementCreated = announcement

export const apiKeyCreated = apiKey.extend({
  plaintextKey: z.string(),
})

// ---------- Company Admin: Custom field defs ----------

export const taskCustomFieldType = z.enum([
  'text',
  'number',
  'date',
  'select',
  'multi_select',
  'url',
  'currency',
  'checkbox',
])

export const taskCustomFieldDef = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  type: taskCustomFieldType,
  options: z.array(z.string()).default([]),
  isRequired: z.boolean(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export const taskCustomFieldDefsResponse = z.object({
  data: z.array(taskCustomFieldDef),
})

// ---------- Company Admin: AI usage ----------

/** Per-month rollup — retained for back-compat with any consumer
 *  that still references the old `byMonth` shape. The dashboard
 *  chart now reads `byWeek` (Sun→Sat current-week breakdown). */
export const aiUsageMonth = z.object({
  month: z.string(),
  total: z.number().int(),
  successful: z.number().int(),
  inputChars: z.number().int(),
  outputChars: z.number().int(),
})

/** Per-weekday rollup — dense 7-entry array, Sun→Sat, for the
 *  current calendar week. The dashboard chart renders this. */
export const aiUsageWeekDay = z.object({
  day: z.string(), // 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'
  dayIndex: z.number().int().min(0).max(6),
  total: z.number().int(),
  successful: z.number().int(),
  inputChars: z.number().int(),
  outputChars: z.number().int(),
})
export const aiUsageRecent = z.object({
  id: z.string().uuid(),
  actorId: z.string().uuid().nullable().optional(),
  actorName: z.string().nullable().optional(),
  kind: z.string(),
  provider: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  latencyMs: z.number().int().nullable().optional(),
  succeeded: z.boolean(),
  createdAt: z.string(),
})
export const aiUsageResponse = z.object({
  /** Monthly AI token cap from the tenant's plan. `null` = unlimited
   *  (enterprise tier or a custom plan without a cap configured). */
  quota: z.number().int().nullable(),
  /** Estimated tokens used so far this calendar month (chars / 4). */
  currentMonthTotal: z.number().int(),
  /** Raw AI call count this month — included alongside tokens so the
   *  UI can show "N calls / X tokens" if it wants. Optional + 0
   *  default so older API responses still parse. */
  currentMonthCalls: z.number().int().optional().default(0),
  quotaUsedRate: z.number().int(),
  /** Tenant's plan code. Surfaced so the UI can label the progress
   *  bar with the plan name. Optional for back-compat. */
  planTier: z.string().nullable().optional(),
  /** @deprecated Older API surface kept for back-compat. The
   *  dashboard chart reads `byWeek` instead. Optional so newer API
   *  responses (which omit `byMonth`) still parse cleanly. */
  byMonth: z.array(aiUsageMonth).optional().default([]),
  /** Sun→Sat current-week breakdown. Always 7 entries; days with no
   *  activity carry `total: 0`. Optional + default so the schema
   *  parses against API builds that pre-date the field. */
  byWeek: z.array(aiUsageWeekDay).optional().default([]),
  byKind: z.array(z.object({ kind: z.string(), total: z.number().int() })),
  recent: z.array(aiUsageRecent),
})

// ---------- Company Admin: Exit handover ----------

export const exitHandoverPreview = z.object({
  leavingUser: z.object({
    id: z.string().uuid(),
    fullName: z.string(),
    emailId: z.string().email(),
    status: z.string(),
    departmentId: z.string().uuid().nullable().optional(),
  }),
  tasksAssigned: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      status: z.string(),
      listId: z.string().uuid(),
      spaceId: z.string().uuid(),
      spaceName: z.string(),
    }),
  ),
  tasksCreated: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      status: z.string(),
      listId: z.string().uuid(),
      spaceId: z.string().uuid(),
      spaceName: z.string(),
    }),
  ),
  pendingApprovals: z.array(
    z.object({ id: z.string().uuid(), name: z.string() }),
  ),
  spaceMemberships: z.array(
    z.object({ spaceId: z.string().uuid(), spaceName: z.string() }),
  ),
  // Department-scoped handover candidates. The backend pre-filters
  // to users who share at least one department with the leaver
  // (falling back to all active tenant users when no shared dept
  // exists, so the admin can still operate in single-person depts).
  // Optional fields so older API builds that don't ship them yet
  // still parse — the dialog has a fallback path for that case.
  candidates: z
    .array(
      z.object({
        id: z.string().uuid(),
        fullName: z.string(),
        emailId: z.string().email(),
        status: z.string(),
        sharedDepartments: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  fallbackToAll: z.boolean().optional(),
  leavingDepartmentIds: z.array(z.string().uuid()).optional(),
  summary: z.object({
    tasksAssignedCount: z.number().int(),
    tasksCreatedCount: z.number().int(),
    pendingApprovalsCount: z.number().int(),
    spaceMembershipsCount: z.number().int(),
  }),
})

export const exitHandoverResult = z.object({
  offboarded: z.unknown(),
  reassignedAssignments: z.number().int(),
  reassignedCreated: z.number().int(),
  // Added 2026-05-22 alongside the fix for the SpaceMember E11000
  // crash. Optional so older API builds (that still return only the
  // first two counters) don't fail Zod validation on the frontend.
  reassignedApprovals: z.number().int().optional(),
  reassignedMemberships: z.number().int().optional(),
})

// ---------- External collaborator: handover-on-revoke ----------

// Preview for revoking an external collaborator — the workload that would move
// and the internal colleagues who could receive it. `hasWorkload` drives
// whether the modal REQUIRES a destination.
export const externalHandoverPreview = z.object({
  summary: z.object({
    tasksAssignedCount: z.number().int(),
    tasksCreatedCount: z.number().int(),
    pendingApprovalsCount: z.number().int(),
    goalsCount: z.number().int(),
  }),
  candidates: z
    .array(
      z.object({
        id: z.string(),
        fullName: z.string(),
        emailId: z.string(),
        status: z.string(),
        sharedDepartments: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  fallbackToAll: z.boolean().optional(),
  hasWorkload: z.boolean(),
})

export const externalRevokeResult = z.object({
  seatId: z.string(),
  status: z.string(),
  reassigned: z
    .object({
      reassignedAssignments: z.number().int(),
      reassignedCreated: z.number().int(),
      reassignedApprovals: z.number().int().optional(),
      reassignedGoals: z.number().int().optional(),
    })
    .nullable()
    .optional(),
})

// ---------- Company Admin: CSV import preview ----------

export const csvImportPreview = z.object({
  headers: z.array(z.string()),
  valid: z.array(
    z.object({
      name: z.string(),
      description: z.string().nullable().optional(),
      status: z.string().nullable().optional(),
      priority: z.string().nullable().optional(),
      dueDate: z.string().nullable().optional(),
      startDate: z.string().nullable().optional(),
      spaceName: z.string(),
      listName: z.string(),
    }),
  ),
  invalid: z.array(
    z.object({
      row: z.number().int(),
      errors: z.array(z.string()),
      data: z.record(z.string(), z.unknown()),
    }),
  ),
  errors: z.array(z.string()),
})

export const csvImportResult = z.object({
  inserted: z.number().int(),
  invalidRows: z.number().int(),
  skipped: z.array(
    z.object({ name: z.string(), reason: z.string() }),
  ),
})

// ---------- Company Admin: Performance metrics ----------

export const performanceOverview = z.object({
  windowDays: z.number().int(),
  onTimeRate: z.number().int(),
  onTimeApproved: z.number().int(),
  totalApproved: z.number().int(),
  throughput: z.number().int(),
  avgCycleTimeHours: z.number().nullable().optional(),
  reworkRate: z.number().int(),
  decisions: z.number().int(),
  rejected: z.number().int(),
  timeMinutes: z.number().int(),
  pointsAwarded: z.number().int(),
  /** Same-length prior window (e.g. the previous 30 days when the
   *  current window is the last 30 days). Drives the "↑ 12% vs prior
   *  30 days" delta badge on each headline KPI. Optional so legacy
   *  responses without it still parse. */
  previous: z
    .object({
      throughput: z.number().int(),
      onTimeRate: z.number().int(),
      reworkRate: z.number().int(),
      avgCycleTimeHours: z.number().nullable().optional(),
    })
    .optional(),
  throughputByDepartment: z.array(
    z.object({
      departmentId: z.string().uuid(),
      departmentName: z.string(),
      throughput: z.number().int(),
    }),
  ),
  /** Per-employee point records — every user who earned at least one
   *  point award in the selected window. Sorted by total points DESC. */
  pointsByUser: z
    .array(
      z.object({
        userId: z.string().uuid(),
        fullName: z.string(),
        /** Work-role tier the user holds inside this tenant.
         *  Optional/nullable so legacy users without a tier still parse. */
        workRole: z
          .enum(['employee', 'team_leader', 'manager'])
          .nullable()
          .optional(),
        /** The user's ACTUAL assigned Work Role name (operations-driven).
         *  Null when on no custom role. Prefer this over `workRole` for labels. */
        workRoleName: z.string().nullable().optional(),
        departmentId: z.string().uuid().nullable(),
        departmentName: z.string().nullable(),
        points: z.number().int(),
        /** Number of point-award rows in the window — i.e. how many
         *  separate approvals contributed to the total. */
        awards: z.number().int(),
        /** Points earned from delivering tasks (the owner-award flow).
         *  Optional + default 0 so older payloads still parse. */
        ownerPoints: z.number().int().optional().default(0),
        /** Historical review-credit points (legacy approval flow).
         *  Optional + default 0 so older payloads still parse. */
        approvalPoints: z.number().int().optional().default(0),
        /** Legacy team-rollup share — no longer awarded, but historical
         *  rows may still report it. Optional + default 0. */
        rollupPoints: z.number().int().optional().default(0),
      }),
    )
    .default([]),
  /** Per-Work-Role aggregation (operations-driven). One row per assigned
   *  Work Role NAME the Company Admin created — plus a single "Unassigned"
   *  row for members on no custom role. Sorted by points DESC.
   *  Optional so responses from older API builds still parse. */
  pointsByRole: z
    .array(
      z.object({
        id: z.string().nullable(),
        name: z.string(),
        users: z.number().int(),
        points: z.number().int(),
        ownerPoints: z.number().int(),
        approvalPoints: z.number().int(),
      }),
    )
    .optional(),
})

/** Personal performance dashboard payload — backs the role-aware
 *  Performance > My view. Built on top of `myStats` so the existing
 *  `total` / `recent` / `badges` fields still match. */
export const myPerformance = z.object({
  total: z.number().int(),
  recent: z.array(z.object({
    id: z.string(),
    points: z.number().int(),
    reason: z.string(),
    awardedAt: z.union([z.string(), z.date()]).nullable().optional(),
    taskId: z.string().uuid().nullable().optional(),
  })),
  badges: z.array(z.object({
    id: z.string(),
    badgeCode: z.string(),
    awardedAt: z.union([z.string(), z.date()]).nullable().optional(),
  })),
  workRole: z.enum(['employee', 'team_leader', 'manager']).nullable(),
  role: z.string(),
  ownerPoints: z.number().int(),
  ownerTasks: z.number().int(),
  approvalPoints: z.number().int(),
  approvalsIssued: z.number().int(),
  rollupPoints: z.number().int().optional().default(0),
  rollupTasks: z.number().int().optional().default(0),
  reversed: z.number().int(),
  leaderboardRank: z.number().int().nullable(),
})

// ---------- Platform Admin: Platform modules ----------

// Plans — every cap field is `number | null` where `null` means
// "unlimited" (matches the billing/limits.js convention).
export const plan = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  // Plan type discriminator (mirrors ProcessPro). Optional/default
  // 'paid' so older responses parse cleanly.
  type: z.enum(['trial', 'paid']).optional().default('paid'),
  monthlyPrice: z.union([z.string(), z.number()]).nullable().optional(),
  yearlyPrice: z.union([z.string(), z.number()]).nullable().optional(),
  currency: z.string().length(3),
  // Headline caps shown on the card.
  maxUsers: z.number().int().nullable().optional(),
  maxSops: z.number().int().nullable().optional(),
  aiTokensPerMonth: z.number().int().nullable().optional(),
  // Advanced caps.
  storageGb: z.number().int().nullable().optional(),
  maxWorkspaces: z.number().int().nullable().optional(),
  maxSpacesPerWorkspace: z.number().int().nullable().optional(),
  maxTasks: z.number().int().nullable().optional(),
  maxDocuments: z.number().int().nullable().optional(),
  maxApiKeys: z.number().int().nullable().optional(),
  // Trial offered when picking this plan at Sign-up.
  trialDays: z.number().int().nullable().optional(),
  featuresList: z.array(z.string()).optional().default([]),
  features: z.record(z.string(), z.unknown()),
  isActive: z.boolean(),
  // Draft plans are hidden from Sign-up until published. Optional/
  // default-false so rows from before the column read as published.
  isDraft: z.boolean().optional().default(false),
  // Public plans appear on the Sign-up picker; private plans are
  // assignable only by Platform Admin. Optional/default-true for
  // backward compatibility with rows from before the column existed.
  isPublic: z.boolean().optional().default(true),
  isDefault: z.boolean(),
  // "Most Popular" badge toggle. Optional/default-false for rows from
  // before the column existed.
  isPopular: z.boolean().optional().default(false),
  // Enterprise / custom-pricing flag — when set the website shows a
  // "Talk to sales" button instead of self-serve checkout. Optional/
  // default-false for rows from before the column existed.
  isContactSales: z.boolean().optional().default(false),
  sortOrder: z.number().int(),
  stripeMonthlyPriceId: z.string().nullable().optional(),
  stripeYearlyPriceId: z.string().nullable().optional(),
  // Count of tenants whose `planTier` matches this plan's `code`.
  // Server-side; pages don't have to compute it.
  activeCompanyCount: z.number().int().optional().default(0),
  // Name-aware trial classification from the server (isTrialPlan SSOT):
  // true when type is 'trial' OR the name/code contains "trial" (e.g. a
  // "Free Trial" plan saved as type:'paid'). Drives the Plans page Trial
  // filter + KPI so they agree with the Companies-page "Trial" pill.
  isTrial: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export const plansResponse = z.object({
  data: z.array(plan),
  /** True platform-wide count of status-active tenants — NOT the sum
   *  of per-plan activeCompanyCount (which misses tenants on legacy /
   *  deleted plan codes). Optional so older API payloads still parse. */
  activeTenantCount: z.number().int().optional(),
})

/** Public sign-up plan — same shape as `plan` but without Stripe IDs
 *  and `features` blob (the unauthenticated endpoint strips those). */
export const publicPlan = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  type: z.enum(['trial', 'paid']).optional().default('paid'),
  monthlyPrice: z.union([z.string(), z.number()]).nullable().optional(),
  yearlyPrice: z.union([z.string(), z.number()]).nullable().optional(),
  currency: z.string().length(3),
  // Headline trio for the Sign-up picker card.
  maxUsers: z.number().int().nullable().optional(),
  maxWorkspaces: z.number().int().nullable().optional(),
  storageGb: z.number().int().nullable().optional(),
  // Secondary — kept so the picker can surface them in the feature
  // bullets if a plan's `featuresList` is empty.
  maxSops: z.number().int().nullable().optional(),
  aiTokensPerMonth: z.number().int().nullable().optional(),
  featuresList: z.array(z.string()).optional().default([]),
  trialDays: z.number().int().nullable().optional(),
  isDefault: z.boolean(),
  isPopular: z.boolean().optional().default(false),
  // Enterprise / contact-sales flag — these are sales-led, so the
  // self-serve sign-up picker hides them (prospects reach them via the
  // "Talk to sales" button on the marketing site). Default false for
  // rows from before the column existed.
  isContactSales: z.boolean().optional().default(false),
  sortOrder: z.number().int(),
})
export const publicPlansResponse = z.object({ data: z.array(publicPlan) })

// Coupons
export const coupon = z.object({
  id: z.string().uuid(),
  code: z.string(),
  description: z.string().nullable().optional(),
  discountType: z.enum(['percentage', 'amount']),
  discountValue: z.union([z.string(), z.number()]),
  currency: z.string().nullable().optional(),
  maxRedemptions: z.number().int().nullable().optional(),
  redemptionCount: z.number().int(),
  validFrom: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  appliesToPlans: z.array(z.string()).default([]),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdById: z.string().uuid().nullable().optional(),
  createdByName: z.string().nullable().optional(),
})
export const couponsResponse = z.object({ data: z.array(coupon) })

// Leads
export const leadStatusEnum = z.enum([
  'new',
  'contacted',
  'qualified',
  'converted',
  'lost',
])
/** Server-side pagination metadata returned alongside a `data` array. */
export const paginationMeta = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
})

export const lead = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  emailId: z.string().email(),
  company: z.string().nullable().optional(),
  contactNumber: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  status: leadStatusEnum,
  notes: z.string().nullable().optional(),
  assignedToId: z.string().uuid().nullable().optional(),
  assignedToName: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export const leadsResponse = z.object({
  data: z.array(lead),
  pagination: paginationMeta.optional(),
  // Per-status pipeline counts — search-scoped, independent of the
  // status filter so the pipeline pills stay stable.
  statusCounts: z.record(z.string(), z.number()).optional(),
  // Per-source counts — drive the Source filter dropdown.
  sourceCounts: z.record(z.string(), z.number()).optional(),
})

/** Inbound lead-ingestion config (Super Admin). The shared secret + the
 *  per-source endpoint URLs pasted into Meta / Google / N8N / a webhook. */
export const leadIngestConfig = z.object({
  token: z.string(),
  enabled: z.boolean(),
  baseUrl: z.string(),
  endpoints: z.record(z.string(), z.string()),
})

// Audit logs
export const auditLogRow = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid().nullable().optional(),
  tenantName: z.string().nullable().optional(),
  actorId: z.string().uuid().nullable().optional(),
  actorName: z.string().nullable().optional(),
  actorEmail: z.string().nullable().optional(),
  actorRole: z.string().nullable().optional(),
  action: z.string(),
  resourceType: z.string().nullable().optional(),
  resourceId: z.string().nullable().optional(),
  metadata: z.unknown(),
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
  createdAt: z.string(),
})
export const auditLogsResponse = z.object({
  data: z.array(auditLogRow),
  pagination: paginationMeta.optional(),
})

// Support tickets
export const ticketStatusEnum = z.enum([
  'open',
  // A ticket the customer bounced back after resolution/closure — kept
  // distinct from `open` so both panels track and display it as Reopened.
  'reopened',
  'in_progress',
  'waiting_customer',
  'resolved',
  'closed',
])
export const ticketPriorityEnum = z.enum(['low', 'normal', 'high', 'urgent'])
export const ticketCategoryEnum = z.enum([
  'bug',
  'feature_request',
  'billing',
  'general',
])

export const supportTicket = z.object({
  id: z.string().uuid(),
  ticketNumber: z.string().nullable().optional(),
  tenantId: z.string().uuid().nullable().optional(),
  tenantName: z.string().nullable().optional(),
  userId: z.string().uuid().nullable().optional(),
  userName: z.string().nullable().optional(),
  userEmail: z.string().email().nullable().optional(),
  subject: z.string(),
  description: z.string(),
  category: ticketCategoryEnum,
  priority: ticketPriorityEnum,
  status: ticketStatusEnum,
  assignedToId: z.string().uuid().nullable().optional(),
  resolvedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Image attachments (screenshots) uploaded when the ticket was
   *  filed. `url` is an S3 URL when storage is configured, otherwise an
   *  inline data URL. Optional + defaulted so older responses parse. */
  attachments: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
        filename: z.string().nullable().optional(),
        mimeType: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
})
export const supportTicketsResponse = z.object({
  data: z.array(supportTicket),
  // Optional — the tenant-scoped `/support/tickets` endpoint returns no
  // pagination block, only the Platform Admin inbox does.
  pagination: paginationMeta.optional(),
})

export const supportTicketMessage = z.object({
  id: z.string().uuid(),
  ticketId: z.string().uuid(),
  authorId: z.string().uuid().nullable().optional(),
  authorName: z.string().nullable().optional(),
  body: z.string(),
  isInternalNote: z.boolean(),
  createdAt: z.string(),
})

export const supportTicketDetail = supportTicket.extend({
  messages: z.array(supportTicketMessage),
})

// Platform settings
export const platformSetting = z.object({
  key: z.string(),
  value: z.unknown(),
  description: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  updatedById: z.string().uuid().nullable().optional(),
  isOverridden: z.boolean(),
})
export const platformSettingsResponse = z.object({
  data: z.array(platformSetting),
})

// ---------- Platform Admin: Platform overview + cross-tenant users ----------

// 14-day daily aggregate the platform overview emits for sparklines —
// always 14 contiguous {day, count} buckets, zero-filled for any day
// that had no underlying activity.
const platformDaily = z.object({
  day: z.string(),
  count: z.number().int(),
})

export const platformOverviewResponse = z.object({
  tenants: z.object({
    total: z.number().int(),
    byStatus: z.object({
      active: z.number().int(),
      suspended: z.number().int(),
      offboarded: z.number().int(),
    }),
    // Company Status pie — the Companies page's DISPLAY status split: an
    // active company whose owner hasn't accepted the setup invite counts as
    // `invited`. Optional so an older API (no field) still parses.
    byDisplayStatus: z
      .object({
        active: z.number().int(),
        invited: z.number().int(),
        suspended: z.number().int(),
        offboarded: z.number().int(),
      })
      .optional(),
    // Array keyed by Plan.code with the display name + count. Used to
    // be a hardcoded 2-key object that silently dropped custom plans —
    // the dashboard now renders whatever the backend returns.
    byPlan: z.array(
      z.object({
        code: tenantPlanTier,
        name: z.string(),
        sortOrder: z.number(),
        count: z.number().int(),
      }),
    ),
    recent: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        slug: z.string(),
        planTier: tenantPlanTier,
        createdAt: z.string(),
      }),
    ),
    // 14-day daily new-tenant signups for the hero-tile sparkline.
    // `.default([])` keeps older server responses valid.
    signupsSparkline: z.array(platformDaily).default([]),
    // Tenants grouped by detected country (ISO alpha-2 + count), busiest
    // first. `unknown` = rows with no country set. `.default([])` keeps a
    // response from a server predating this field valid.
    byCountry: z
      .array(z.object({ code: z.string(), count: z.number().int() }))
      .default([]),
  }),
  users: z.object({
    total: z.number().int(),
    active: z.number().int(),
    byRole: z.object({
      super_admin: z.number().int(),
      company_admin: z.number().int(),
      user: z.number().int(),
    }),
    // 14-day daily new-user signups (approximation for DAU; signups
    // are what the audit trail naturally captures).
    signupsSparkline: z.array(platformDaily).default([]),
  }),
  workspaces: z.object({
    total: z.number().int(),
    // 14-day new-workspace creation series for the tile sparkline.
    // Optional + defaulted so older API responses still parse.
    createdSparkline: z.array(platformDaily).default([]),
  }),
  reports: z.object({ last30: z.number().int() }),
  points: z.object({ total: z.number().int() }),
  badges: z.object({ total: z.number().int() }),
  // Platform-wide audit feed used by the Recent Events card and the
  // Platform Events KPI tile sparkline. `.default({})` keeps older
  // responses valid — the dashboard renders an empty state if the
  // events block is missing.
  events: z
    .object({
      recent: z
        .array(
          z.object({
            id: z.string(),
            action: z.string(),
            actorRole: z.string().nullable().optional(),
            actorName: z.string().nullable().optional(),
            tenantName: z.string().nullable().optional(),
            resourceType: z.string().nullable().optional(),
            createdAt: z.string(),
          }),
        )
        .default([]),
      eventsSparkline: z.array(platformDaily).default([]),
    })
    .default({ recent: [], eventsSparkline: [] }),
  // Top 8 tenants by storage consumption. `percentOfCap` is null
  // when the tenant's plan has no storage cap (unlimited).
  storageUsageByTenant: z
    .array(
      z.object({
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        usedBytes: z.number().nonnegative(),
        percentOfCap: z.number().nullable(),
        planTier: z.string().nullable(),
      }),
    )
    .optional(),
  // Top 8 tenants by AI credit spend this calendar month, broken
  // out by provider so the dashboard can render a stacked view
  // without a second round-trip.
  aiUsageByTenant: z
    .array(
      z.object({
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        creditsThisMonth: z.number().int().nonnegative(),
        modelMix: z.object({
          anthropic: z.number().int().nonnegative(),
          google: z.number().int().nonnegative(),
          heuristic: z.number().int().nonnegative(),
        }),
      }),
    )
    .optional(),
  // Platform-wide support-ticket health. Always emitted (even with
  // zero counts) so the card has a stable shape.
  supportTickets: z
    .object({
      openCount: z.number().int().nonnegative(),
      urgentCount: z.number().int().nonnegative(),
      overdueCount: z.number().int().nonnegative(),
      recent: z.array(
        z.object({
          ticketId: z.string(),
          subject: z.string(),
          tenantName: z.string().nullable(),
          priority: z.string(),
          status: z.string(),
          createdAt: z.union([z.string(), z.date()]),
        }),
      ),
    })
    .optional(),
  // Composite risk list — storage-over-cap, ai-hot, payment-failed,
  // trial-ending. Capped at 12 server-side and ordered danger →
  // warning so the dashboard can render top-N without re-sorting.
  risks: z
    .array(
      z.object({
        kind: z.enum([
          'storage-over-cap',
          'ai-hot',
          'payment-failed',
          'trial-ending',
        ]),
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        severity: z.enum(['warning', 'danger']),
        detail: z.string(),
      }),
    )
    .optional(),
  // Real liveness probes. Each subsystem reports one of three states:
  //   ok=true   → green tile, with whatever metric the probe captured
  //               (uptime, latency, queue depth)
  //   ok=false  → red tile, with the probe error in `error`
  //   ok=null   → grey "Not monitored" tile, with explanation in `note`
  //               (e.g. Redis not configured in this deployment)
  // Schemas are deliberately permissive on extra metric fields so the
  // backend can add new ones (e.g. memoryMb) without a schema break.
  systemHealth: z
    .object({
      api: z
        .object({
          ok: z.boolean().nullable(),
          uptimeSec: z.number().optional(),
          memoryMb: z.number().optional(),
          error: z.string().optional(),
          note: z.string().optional(),
          lastCheckedAt: z.string(),
        })
        .nullable(),
      database: z
        .object({
          ok: z.boolean().nullable(),
          latencyMs: z.number().optional(),
          error: z.string().optional(),
          note: z.string().optional(),
          lastCheckedAt: z.string(),
        })
        .nullable(),
      redis: z
        .object({
          ok: z.boolean().nullable(),
          latencyMs: z.number().optional(),
          error: z.string().optional(),
          note: z.string().optional(),
          lastCheckedAt: z.string(),
        })
        .nullable(),
      emailQueue: z
        .object({
          ok: z.boolean().nullable(),
          queueDepth: z.number().int().nonnegative().optional(),
          failed: z.number().int().nonnegative().optional(),
          active: z.number().int().nonnegative().optional(),
          error: z.string().optional(),
          note: z.string().optional(),
          lastCheckedAt: z.string(),
        })
        .nullable(),
      storage: z
        .object({
          ok: z.boolean().nullable(),
          latencyMs: z.number().optional(),
          error: z.string().optional(),
          note: z.string().optional(),
          lastCheckedAt: z.string(),
        })
        .nullable()
        .optional(),
      stripe: z
        .object({
          ok: z.boolean().nullable(),
          latencyMs: z.number().optional(),
          error: z.string().optional(),
          note: z.string().optional(),
          lastCheckedAt: z.string(),
        })
        .nullable()
        .optional(),
      ai: z
        .object({
          ok: z.boolean().nullable(),
          error: z.string().optional(),
          note: z.string().optional(),
          lastCheckedAt: z.string(),
        })
        .nullable()
        .optional(),
    })
    .optional(),
  // Per-runner background-job heartbeats for the "Scheduled Jobs" strip.
  scheduledJobs: z
    .array(
      z.object({
        name: z.string(),
        lastRunAt: z.string().nullable(),
        lastDurationMs: z.number().nullable().optional(),
        lastOk: z.boolean().nullable().optional(),
        lastError: z.string().nullable().optional(),
        everyMs: z.number().nullable().optional(),
        runCount: z.number().optional(),
      }),
    )
    .optional(),
})

export const platformUserRow = z.object({
  id: z.string().uuid(),
  // Platform Admins live outside any tenant — their row comes back
  // with null tenant fields. The UI renders a "Platform" pill in
  // place of the company link.
  tenantId: z.string().uuid().nullable(),
  tenantName: z.string().nullable().optional(),
  tenantPlanTier: tenantPlanTier.nullable().optional(),
  tenantStatus: z.enum(['active', 'suspended', 'offboarded']).nullable().optional(),
  emailId: z.string().email(),
  fullName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  role: userRole,
  workRole: workRole.optional().default('employee'),
  // Resolved ASSIGNED Work Role name (the custom catalogue name, or a tier
  // label fallback). `workRole` above is the inert tier slug, which reads
  // "Employee" for every operations-driven custom role.
  workRoleName: z.string().nullable().optional(),
  status: z.enum(['invited', 'active', 'suspended', 'offboarded']),
  lastActiveAt: z.string().nullable().optional(),
  createdAt: z.string(),
  // Workspace(s) the user is a member of. Empty for Company / Sub
  // Admins (tenant-level, no per-workspace membership) → the row
  // renders "—". `status` lets the cell mute inactive workspaces.
  workspaces: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        status: z.enum(['active', 'inactive']).nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  // Department name(s) the user belongs to (deduped across workspaces).
  // Empty for Company / Sub Admins (tenant-level, no membership) and for
  // members not assigned to any department → the row renders "—".
  departments: z.array(z.string()).optional().default([]),
})

export const platformUsersResponse = z.object({
  data: z.array(platformUserRow),
  // Filter-wide KPI totals computed server-side over the whole matched
  // set (this page is server-paginated, so the client can't recompute
  // them from `data`). Optional so older responses still parse.
  summary: z
    .object({
      total: z.number().int(),
      activeTotal: z.number().int(),
      tenantsRepresentedTotal: z.number().int(),
      // Unfiltered platform-wide grand total (users across every tenant,
      // excluding Platform Admins). Powers the "USERS (TOTAL)" tile which
      // must not change when the admin applies a status / role / company
      // filter. Optional so older API responses still parse.
      platformTotal: z.number().int().optional(),
      // Per-status distribution over the filtered set BUT ignoring the
      // status filter — powers per-status KPI tiles (Active / Invited /
      // Suspended / Offboarded). Counts the user's EFFECTIVE status, so a
      // suspended company's users land under Suspended (not a separate
      // "Inactive" tile). Optional so older API responses still parse; the
      // frontend falls back to hiding those tiles when absent.
      statusCounts: z
        .object({
          active: z.number().int(),
          invited: z.number().int(),
          suspended: z.number().int(),
          offboarded: z.number().int(),
        })
        .optional(),
    })
    .optional(),
  pagination: paginationMeta.optional(),
})

// ---------- Platform Admin: Tenants management ----------

export const platformTenantRow = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  // [QA-15] The company's IANA timezone — Analytics resolves a drilled
  // company's period edges in it. Optional so older responses still parse.
  timezone: z.string().nullable().optional(),
  status: z.enum(['active', 'suspended', 'offboarded']),
  // Display-only status for the Companies table. Same as `status`, except
  // it reads 'invited' while an active company's owner hasn't accepted the
  // setup invite yet. The stored tenant status stays active — this is UI only.
  displayStatus: z
    .enum(['active', 'suspended', 'offboarded', 'invited'])
    .optional(),
  planTier: tenantPlanTier,
  // Server resolves `planTier` to the Plan row's display name when
  // returning the list. `null` when the tenant has no plan or carries a
  // retired/unseeded code — the page renders a "No plan" pill for those.
  planName: z.string().nullable().optional(),
  planSortOrder: z.number().int().optional(),
  features: tenantFeatures,
  // How the tenant was created — drives the Origin pill on the
  // Companies table (`super_admin` → "Master Admin", everything else
  // → "Self signup"). MUST be declared here: Zod strips undeclared
  // keys, so without this the backend's `createdVia` is dropped from
  // every row and every company wrongly reads as "Self signup".
  // Kept as a permissive string (not a strict enum) + default so a
  // future origin value can't fail the whole list parse — OriginCell
  // already falls back gracefully for unrecognised values.
  createdVia: z.string().optional().default('self_signup'),
  createdAt: z.string(),
  updatedAt: z.string(),
  memberCount: z.number().int(),
  // Subset of memberCount — number of pending invitations the tenant
  // hasn't accepted yet. Drives the "(N pending)" tooltip on the
  // Users column. Optional so older responses still parse.
  pendingInviteCount: z.number().int().optional().default(0),
  workspaceCount: z.number().int(),
  // SOP count is aggregated from Sop → List → Space → Workspace → tenantId
  // server-side. Optional so older responses (or tenants with no SOPs at
  // all) parse cleanly.
  sopCount: z.number().int().optional().default(0),
  // Total non-deleted file bytes for the tenant — drives the
  // "Storage" column on the Companies table. Optional so old
  // responses without this field still parse.
  storageBytes: z.number().int().optional().default(0),
  // Estimated AI tokens this calendar month — drives the "AI Usage"
  // column on the Companies table. ceil(chars / 4), the same estimate
  // the AI Usage report uses. Optional so old responses parse.
  aiTokensThisMonth: z.number().int().nonnegative().optional().default(0),
  // Billing currency — INR / AED / USD. Drives the legacy
  // currency-proxy fallback in the Country column when `country`
  // is null. Optional + nullable so older API responses parse.
  billingCurrency: z.string().max(3).nullable().optional(),
  // ISO 3166-1 alpha-2 country code. Stamped at signup from
  // geoip-lite, overwritten by the Stripe webhook from the billing
  // address. Null for legacy rows / tenants whose IP couldn't be
  // geo-resolved; the Companies-page CountryCell falls back to
  // the currency proxy in that case.
  country: z.string().length(2).nullable().optional(),
  countrySource: z
    .enum(['ip', 'billing', 'manual'])
    .nullable()
    .optional(),
  // Trial / subscription window — both nullable because most tenants
  // aren't on Stripe yet. The Companies page renders a "No trial"
  // placeholder when either is missing.
  subscriptionStatus: z
    .enum([
      'incomplete',
      'incomplete_expired',
      'trialing',
      'active',
      'past_due',
      'canceled',
      'unpaid',
      'paused',
    ])
    .nullable()
    .optional(),
  subscriptionPeriodEnd: z.string().nullable().optional(),
})

export const platformTenantsResponse = z.object({
  data: z.array(platformTenantRow),
  pagination: paginationMeta.optional(),
  /**
   * Platform-wide tenant rollup driven by the Companies page's KPI
   * card strip. Always returned for the page-mode (`paginate=true`)
   * call. The backend deliberately ignores the current
   * search / status / plan filter when computing this — the cards
   * are the "platform health at a glance" summary, the table below
   * is where the filtered exploration happens. `byCountry` keys
   * mirror the spec's three target markets (§1): India (INR-billed),
   * UAE (AED-billed), Global (USD-billed). Optional on the schema
   * to keep older non-page callers that don't carry the summary
   * backward-compatible.
   */
  summary: z
    .object({
      total: z.number().int(),
      active: z.number().int(),
      inactive: z.number().int(),
      // Tenants currently in 'trialing' lifecycle bucket. Drives
      // the 4th KPI card (Trial) on the Companies page.
      trial: z.number().int(),
      byCountry: z.object({
        india: z.number().int(),
        uae: z.number().int(),
        global: z.number().int(),
      }),
      // 14-day sparkline series per card. Each entry is one bucket
      // per ISO day; gaps are pre-filled with zero server-side so
      // the array is always exactly 14 points wide. Fed straight
      // into the inline Sparkline that paints under each card's
      // headline number.
      sparklines: z.object({
        total: z.array(z.object({ day: z.string(), count: z.number().int() })),
        active: z.array(z.object({ day: z.string(), count: z.number().int() })),
        inactive: z.array(
          z.object({ day: z.string(), count: z.number().int() }),
        ),
        trial: z.array(z.object({ day: z.string(), count: z.number().int() })),
      }),
    })
    .optional(),
})

// Cross-tenant AI usage — backs the Platform Admin "AI Usage" page.
// Tenants with zero events still appear so the table reads as the
// full population, not just heavy users.
export const platformAiUsageRow = z.object({
  id: z.string().uuid(),
  name: z.string(),
  planTier: tenantPlanTier,
  status: z.enum(['active', 'suspended', 'offboarded']),
  // Real ISO alpha-2 country, with billing-currency proxy fallback —
  // backs the Country column / filter, same shape the Companies row
  // uses.
  country: z.string().length(2).nullable().optional(),
  billingCurrency: z.string().max(3).nullable().optional(),
  callCount: z.number().int(),
  successCount: z.number().int(),
  inputChars: z.number().int(),
  outputChars: z.number().int(),
  estTokens: z.number().int(),
  monthlyAiQuotaTokens: z.number().int().nullable(),
  quotaPct: z.number().nullable(),
  lastCallAt: z.string().nullable(),
})
export const platformAiUsageResponse = z.object({
  range: z.object({
    from: z.string(),
    to: z.string(),
    days: z.number().int(),
  }),
  totals: z.object({
    callCount: z.number().int(),
    successCount: z.number().int(),
    inputChars: z.number().int(),
    outputChars: z.number().int(),
    estTokens: z.number().int(),
    activeTenants: z.number().int(),
    tenantCount: z.number().int(),
  }),
  data: z.array(platformAiUsageRow),
  pagination: paginationMeta.optional(),
})

// Cross-tenant storage — backs the Platform Admin "Storage" page.
export const platformStorageRow = z.object({
  id: z.string().uuid(),
  name: z.string(),
  planTier: tenantPlanTier,
  status: z.enum(['active', 'suspended', 'offboarded']),
  // Real ISO alpha-2 country, with billing-currency proxy fallback —
  // backs the Country column / filter, same shape the Companies row
  // uses.
  country: z.string().length(2).nullable().optional(),
  billingCurrency: z.string().max(3).nullable().optional(),
  usedBytes: z.number().int(),
  fileCount: z.number().int(),
  // Nullable for plans with no cap (enterprise / unlimited).
  quotaBytes: z.number().int().nullable(),
  pctUsed: z.number().nullable(),
  overQuota: z.boolean(),
})
export const platformStorageResponse = z.object({
  totals: z.object({
    usedBytes: z.number().int(),
    fileCount: z.number().int(),
    tenantsOverQuota: z.number().int(),
    tenantCount: z.number().int(),
  }),
  data: z.array(platformStorageRow),
  pagination: paginationMeta.optional(),
})

export const platformTenantDetail = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  status: z.enum(['active', 'suspended', 'offboarded']),
  planTier: tenantPlanTier,
  features: tenantFeatures,
  timezone: z.string().nullable().optional(),
  // Subscription window — mirrors what the Companies list returns so
  // the detail page can render the same Trial pill.
  subscriptionStatus: z
    .enum([
      'incomplete',
      'incomplete_expired',
      'trialing',
      'active',
      'past_due',
      'canceled',
      'unpaid',
      'paused',
    ])
    .nullable()
    .optional(),
  subscriptionPeriodEnd: z.string().nullable().optional(),
  stripeCustomerId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Owner — the first Company Admin on this tenant. Nullable because a
  // tenant could be in a half-migrated state with no admin row.
  owner: z
    .object({
      id: z.string().uuid(),
      fullName: z.string(),
      emailId: z.string().email(),
      contactNumber: z.string().nullable().optional(),
      status: z.enum(['invited', 'active', 'suspended', 'offboarded']),
      lastActiveAt: z.string().nullable().optional(),
    })
    .nullable(),
  metrics: z.object({
    memberCount: z.number().int(),
    workspaceCount: z.number().int(),
    reportCount: z.number().int(),
    taskCount: z.number().int(),
    totalPoints: z.number().int(),
    badgeCount: z.number().int(),
    // New tiles for the detail page — all optional so older responses
    // still parse cleanly during a partial rollout.
    sopCount: z.number().int().optional().default(0),
    fileCount: z.number().int().optional().default(0),
    storageBytes: z.number().int().optional().default(0),
    aiCallsThisMonth: z.number().int().optional().default(0),
    aiTokensThisMonth: z.number().int().optional().default(0),
  }),
})

// ---------- Tenant self-service profile (Phase 1 endpoint) ----------

export const tenantProfile = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  avatarUrl: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  planTier: z.string(),
  // Zod v4 requires a key schema as the first argument to z.record;
  // omitting it (the v3 short form) crashes parse() with a cryptic
  // "_zod" undefined error and silently breaks every consumer.
  features: z.record(z.string(), z.any()).optional(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

// ---------- Workspace templates (onboarding picker) ----------

export const workspaceTemplate = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  /** Lucide icon name string (e.g. 'Layers'). The picker resolves it
   *  to a real component via a small icon map. */
  icon: z.string().optional(),
  spaceCount: z.number().int().nonnegative(),
  listCount: z.number().int().nonnegative(),
  taskCount: z.number().int().nonnegative(),
})

export const workspaceTemplatesResponse = z.object({
  data: z.array(workspaceTemplate),
})

// ---------- Files storage summary (tenant-level rollup) ----------
//
// Returned by GET /files/storage. Used by the Files page and the
// Company Admin Dashboard's storage pie chart.
export const filesStorageSummary = z.object({
  usedBytes: z.number().int().nonnegative(),
  // `null` represents an unlimited/enterprise plan with no hard cap.
  // The backend's `files.service.summary()` returns null whenever the
  // plan's `storageGb` is null or undefined; previously this Zod
  // schema rejected the null and crashed dashboard parsing on
  // unlimited tenants. Frontend consumers MUST treat null as
  // "unlimited" (no progress bar, no cap copy).
  totalBytes: z.number().int().nonnegative().nullable(),
  planTier: z.string(),
  breakdown: z.object({
    documents: z.number().int().nonnegative(),
    images: z.number().int().nonnegative(),
    media: z.number().int().nonnegative(),
    archives: z.number().int().nonnegative(),
  }),
})

// ---------- Admin overview (Company Admin Dashboard payload) ----------

/** Single bucket on a daily sparkline — `day` is YYYY-MM-DD. */
const dailySparklinePoint = z.object({
  day: z.string(),
  count: z.number().int().nonnegative(),
})

/** Daily bucket for the leaderboard points sparkline. Same shape as
 *  `dailySparklinePoint` but `count` is a SIGNED integer: a member can
 *  net negative points on a day (penalties / reversals), matching the
 *  signed `points` / `pointsLastPeriod` fields on the row. The count-based
 *  hero sparklines keep their non-negative constraint. */
const dailyPointsPoint = z.object({
  day: z.string(),
  count: z.number().int(),
})

export const adminOverviewResponse = z.object({
  members: z.object({
    total: z.number().int(),
    active: z.number().int(),
    eligibleForReports: z.number().int(),
    // Breakdown by Work Role (operations-driven model), matching the Work Role
    // Management page and ordered by the admin-arranged hierarchy. A user with
    // no Work Role linked is counted under their level (e.g. "Employee"), as on
    // the Members page (server: utils/work-role-breakdown.js). Feeds the Sub
    // Admin donut. Optional/default so an older server response still parses.
    byWorkRole: z
      .array(
        z.object({
          // Work Role catalogue id when every member in the slice is linked to
          // that role; null for a slice holding level-only members.
          id: z.string().nullable().optional(),
          name: z.string(),
          count: z.number().int(),
        }),
      )
      .optional()
      .default([]),
    companyAdmins: z.number().int(),
    byDepartment: z.array(
      z.object({
        departmentId: z.string().uuid(),
        departmentName: z.string(),
        count: z.number().int(),
      }),
    ),
    activeUsersCount: z.number().int(),
    // 14-day distinct-active-users series. Powers the sparkline behind
    // the "Active Members · 14 Days" hero KPI tile. Optional so older
    // backends still parse — the frontend falls back to a flat tile.
    activeUsersSparkline: z.array(dailySparklinePoint).optional(),
  }),
  departments: z.object({
    total: z.number().int(),
    list: z.array(
      z.object({ id: z.string().uuid(), name: z.string() }),
    ),
  }),
  tasks: z.object({
    total: z.number().int(),
    byApproval: z.object({
      none: z.number().int(),
      pending: z.number().int(),
      approved: z.number().int(),
      rejected: z.number().int(),
    }),
    approvedLast30: z.number().int(),
    // Tasks NOT in done/closed/cancelled categories — drives the
    // first hero tile. Optional so an older API still parses.
    openCount: z.number().int().nonnegative().optional(),
    completedToday: z.number().int().nonnegative().optional(),
    completedThisWeek: z.number().int().nonnegative().optional(),
    // All-time completed (delivered) task count — drives the "Completed
    // Tasks" hero tile, verifiable via the Task Monitor Completed filter.
    completedCount: z.number().int().nonnegative().optional(),
    completedLastWeek: z.number().int().nonnegative().optional(),
    completedSparkline: z.array(dailySparklinePoint).optional(),
    // 14-day series of new top-level tasks created per day. Powers
    // the sparkline behind the "Open Tasks" hero KPI tile. Optional
    // so older backends still parse — the frontend falls back to a
    // flat tile.
    createdSparkline: z.array(dailySparklinePoint).optional(),
    // "Tasks by Status" card — tasks created on each of the last 14 days,
    // split by the workflow status they are in NOW. One row per status in
    // workflow order, coloured from Configuration → Workflows (server-
    // resolved); an "Other" row (color null) only for labels no workflow
    // defines. Optional so older backends still parse.
    statusTrend: z
      .array(
        z.object({
          name: z.string(),
          color: z.string().nullable().default(null),
          sparkline: z.array(dailySparklinePoint),
        }),
      )
      .optional(),
    // "Repeat & Non-repeat Tasks" section — each side split into Completed /
    // Overdue / On track (sums to the Task Completion figures). Null when the
    // caller's grant hides task analytics; optional so older APIs parse.
    repeatSplit: z
      .object({
        repeat: z.object({
          total: z.number().int().nonnegative(),
          completed: z.number().int().nonnegative(),
          overdue: z.number().int().nonnegative(),
          onTrack: z.number().int().nonnegative(),
        }),
        nonRepeat: z.object({
          total: z.number().int().nonnegative(),
          completed: z.number().int().nonnegative(),
          overdue: z.number().int().nonnegative(),
          onTrack: z.number().int().nonnegative(),
        }),
      })
      .nullable()
      .optional(),
    // Snapshot count of top-level tasks past due AND not yet
    // completed. Powers the headline integer of the "Overdue Tasks"
    // hero KPI tile (which replaced "Active Members · 14 Days").
    // Optional so the schema parses against older API responses.
    overdueCount: z.number().int().nonnegative().optional(),
    // 14-day series of overdue-volume-by-due-date — i.e. for each
    // day, the count of currently-uncompleted top-level tasks whose
    // dueDate landed on that day. Reads as the "incoming overdue"
    // trend. Optional so older backends parse.
    overdueSparkline: z.array(dailySparklinePoint).optional(),
  }),
  approvals: z.object({
    pendingCount: z.number().int(),
    oldestPending: z.array(
      z.object({
        taskId: z.string().uuid(),
        taskName: z.string(),
        // Space context can be null when a task's list/space has been
        // archived or orphaned — the row should still render (with a
        // dashed placeholder) instead of breaking the whole dashboard
        // parse.
        spaceId: z.string().uuid().nullable().optional(),
        spaceName: z.string().nullable().optional(),
        listId: z.string().uuid(),
        requestedAt: z.string().nullable().optional(),
      }),
    ),
  }),
  timeTracking: z.object({
    thisWeekMinutes: z.number().int(),
    lastWeekMinutes: z.number().int().optional(),
    todayMinutes: z.number().int().optional(),
    byDepartment: z.array(
      z.object({
        departmentId: z.string().uuid(),
        departmentName: z.string(),
        minutes: z.number().int(),
      }),
    ),
    // 14-day series of minutes-logged-per-day. Powers the sparkline
    // behind the "Hours Logged · This Week" hero KPI tile. Wire shape
    // is `{day, minutes}` so the frontend can format with `fmtMinutes`.
    // Optional so older backends still parse — the frontend falls
    // back to a flat tile.
    hoursLoggedSparkline: z
      .array(
        z.object({
          day: z.string(),
          minutes: z.number().int().nonnegative(),
        }),
      )
      .optional(),
  }),
  reports: z.object({
    // `complianceRate` is null when `eligible === 0` — i.e. the tenant
    // has no report-eligible members so there's no meaningful ratio to
    // compute. The ComplianceRing renders an explicit empty state for
    // null rather than a misleading 0 %.
    dailyToday: z.object({
      submitted: z.number().int(),
      eligible: z.number().int(),
      complianceRate: z.number().int().nullable(),
    }),
    weeklyThisWeek: z.object({
      submitted: z.number().int(),
      eligible: z.number().int(),
      complianceRate: z.number().int().nullable(),
    }),
    monthlyThisMonth: z.object({
      submitted: z.number().int(),
      eligible: z.number().int(),
      complianceRate: z.number().int().nullable(),
    }),
  }),
  leaderboard: z.array(
    z.object({
      rank: z.number().int(),
      userId: z.string().uuid(),
      points: z.number().int(),
      fullName: z.string(),
      avatarUrl: z.string().nullable().optional(),
      departmentName: z.string().nullable().optional(),
      // Points earned in the previous 30-day window (days 31–60 ago).
      // Drives the delta chip on the row. Optional so older backends
      // (pre-Phase 3) still parse — the frontend then hides the chip.
      pointsLastPeriod: z.number().int().optional(),
      // 14-day per-day point series — feeds the inline gold mini-
      // sparkline. Uses the signed `dailyPointsPoint` shape (points can
      // net negative on a day) but is otherwise identical to the hero KPI
      // sparklines, so the same renderer works without a branch.
      pointsSparkline: z.array(dailyPointsPoint).optional(),
    }),
  ),
  pointsByDepartment: z.array(
    z.object({
      departmentId: z.string().uuid(),
      departmentName: z.string(),
      points: z.number().int(),
    }),
  ),
  // Hours + tasks-shipped per department for the current ISO week.
  // Feeds the grouped-bar productivity chart. The synthetic
  // 'Unassigned' bucket uses a non-UUID id (`__unassigned__`), so the
  // departmentId schema is a permissive string rather than `uuid()`.
  // `tasksPerHour` is null when the dept logged no minutes this week.
  // Optional so older backends (pre-Phase 3) still parse — the
  // frontend renders the previous Hours-only card as fallback.
  productivityByDepartment: z
    .array(
      z.object({
        departmentId: z.string(),
        departmentName: z.string(),
        minutes: z.number().int().nonnegative(),
        tasksCompleted: z.number().int().nonnegative(),
        tasksPerHour: z.number().nullable(),
      }),
    )
    .optional(),
  productivityBenchmark: z
    .object({
      tenantTasksPerHour: z.number().nullable(),
    })
    .nullable()
    .optional(),
  badges: z.object({ totalAwarded: z.number().int() }),
  schedule: reportSchedule.nullable(),
  activity: z.array(
    z.object({
      kind: z.string(),
      title: z.string(),
      // Structured trio that drives the Recent Activity card's
      // avatar + name + description hierarchy. All optional so a
      // stale cache from before the rollout still parses — the card
      // falls back to `title` when `description` is absent and to a
      // kind-tinted icon avatar when `actorName` is absent.
      actorName: z.string().nullable().optional(),
      actorAvatarUrl: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      at: z.string().nullable().optional(),
      link: z.string().nullable().optional(),
      // Coarse bucket assigned server-side based on `kind` prefix.
      // Drives the filter chips above the Recent Activity card. The
      // backend drops items it can't categorise, so this is required
      // on Phase-3+ responses. Marked optional so a stale cache from
      // before the rollout still parses (those items just won't be
      // filtered out by the chip row).
      category: z.enum(['task', 'report', 'comment', 'file', 'badge']).optional(),
    }),
  ),
  // [QA-16] Tenant audit-log counts (same filter as the Audit Log page) for
  // the Sub Admin "Audit Events" KPI. Optional so an older backend still parses.
  audit: z
    .object({
      total: z.number().int(),
      last7Days: z.number().int(),
    })
    .optional(),
  // At-risk task triage list — overdue / due_today / idle, capped at
  // 12 server-side. Optional so an older backend response still
  // parses (the frontend renders an empty card in that case).
  atRiskTasks: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        assigneeName: z.string().nullable(),
        assigneeAvatarUrl: z.string().nullable(),
        dueDate: z.string().nullable(),
        status: z.string(),
        spaceId: z.string().uuid().nullable(),
        listId: z.string().uuid(),
        spaceName: z.string().nullable(),
        listName: z.string().nullable(),
        riskReason: z.enum(['overdue', 'due_today', 'idle']),
        riskHours: z.number().int().nonnegative(),
      }),
    )
    .optional(),
  // First-run setup checklist: each tile carries {done, total,
  // completed} so the same renderer works whether a tile is binary
  // (one signal) or composite (three sub-signals on Company
  // Profile). `overallComplete` / `overallTotal` drive the ring.
  setupChecklist: z
    .object({
      companyProfile: z.object({
        done: z.boolean(),
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
      }),
      departments: z.object({
        done: z.boolean(),
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
      }),
      workRoles: z.object({
        done: z.boolean(),
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
      }),
      members: z.object({
        done: z.boolean(),
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
      }),
      workspaces: z.object({
        done: z.boolean(),
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
      }),
      reportSchedule: z.object({
        done: z.boolean(),
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
      }),
      sopsEnabled: z.object({
        done: z.boolean(),
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
      }),
      overallComplete: z.number().int().nonnegative(),
      overallTotal: z.number().int().nonnegative(),
    })
    .optional(),
  // SOPs rollup. `publishedCount` / `pendingApprovals` are 0 until
  // the Sop model gains a lifecycle column.
  sops: z
    .object({
      totalCount: z.number().int().nonnegative(),
      publishedCount: z.number().int().nonnegative(),
      tasksGenerated: z.number().int().nonnegative(),
      pendingApprovals: z.number().int().nonnegative(),
    })
    .optional(),
  // AI usage rollup. `creditsAllotted` is null when the tenant's
  // plan has no cap (or the cap hasn't been wired through yet).
  aiUsage: z
    .object({
      creditsUsedThisMonth: z.number().int().nonnegative(),
      creditsAllotted: z.number().int().nullable(),
      topConsumerKind: z.string().nullable(),
    })
    .optional(),
})

// ---------- Personal dashboard (Employee landing page payload) -----

/** Single bucket on a personal sparkline. Same wire shape as the
 *  admin variant — `day` is YYYY-MM-DD. */
const personalSparklinePoint = z.object({
  day: z.string(),
  count: z.number().int().nonnegative(),
})

/** Shape of GET /me/dashboard-overview. Powers the polished Employee
 *  landing dashboard introduced 2026-05-22. One round-trip, every
 *  KPI strip + chart + list on the page reads from this payload. */
// Non-repeat / Repeat Task Completion pies — `{ scope, repeat, nonRepeat }`,
// each side `{ total, completed, overdue, onTrack }` (server:
// utils/repeat-split.js). Shared by the team view (workspace-overview, scope
// 'department' / 'self' / 'all') and the self-only dashboard
// (dashboard-overview, scope 'self'). Optional so an older API still parses.
const repeatSplitPart = z.object({
  total: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  onTrack: z.number().int().nonnegative(),
})
export const repeatSplitSchema = z
  .object({
    scope: z.enum(['department', 'self', 'all']).optional().default('department'),
    repeat: repeatSplitPart,
    nonRepeat: repeatSplitPart,
  })
  .nullable()
  .optional()

export const personalDashboardResponse = z.object({
  tasks: z.object({
    openCount: z.number().int().nonnegative(),
    dueTodayCount: z.number().int().nonnegative(),
    overdueCount: z.number().int().nonnegative(),
    completedToday: z.number().int().nonnegative(),
    completedThisWeek: z.number().int().nonnegative(),
    completedLastWeek: z.number().int().nonnegative(),
    completedSparkline: z.array(personalSparklinePoint),
    createdSparkline: z.array(personalSparklinePoint),
    // The viewer's own tasks split by repeat rule (scope 'self').
    repeatSplit: repeatSplitSchema,
    // "To Do" (open, in a workflow's first column) and "Completed Tasks"
    // (all-time, completedAt set) tiles — the team view's definitions over
    // the user's own tasks. Optional so an older API still parses (the
    // tiles then stay hidden rather than reading 0).
    toDoCount: z.number().int().nonnegative().optional(),
    completedCount: z.number().int().nonnegative().optional(),
  }),
  timeTracking: z.object({
    todayMinutes: z.number().int().nonnegative(),
    yesterdayMinutes: z.number().int().nonnegative(),
    thisWeekMinutes: z.number().int().nonnegative(),
    lastWeekMinutes: z.number().int().nonnegative(),
    hoursLoggedSparkline: z.array(
      z.object({
        day: z.string(),
        minutes: z.number().int().nonnegative(),
      }),
    ),
  }),
  streak: z.object({
    currentDays: z.number().int().nonnegative(),
    activitySparkline: z.array(
      z.object({ day: z.string(), active: z.boolean() }),
    ),
  }),
  mentions: z.array(
    z.object({
      id: z.string(),
      taskId: z.string().nullable(),
      taskName: z.string(),
      fromUserId: z.string().nullable(),
      fromUserName: z.string(),
      fromAvatarUrl: z.string().nullable(),
      snippet: z.string(),
      createdAt: z.union([z.string(), z.date()]),
      /** What the row actually is, so the feed labels the action
       *  correctly: `mention` (someone @mentioned you), `reply`
       *  (someone replied to your comment), or `comment` (a plain
       *  comment on your task). Optional + defaulted so older API
       *  responses still parse — untyped rows read as mentions, the
       *  pre-split framing (transitional only; the current backend
       *  always tags the type). */
      type: z
        .enum(['mention', 'reply', 'comment'])
        .optional()
        .default('mention'),
    }),
  ),
  gamification: z.object({
    points: z.number().int(),
    badgesCount: z.number().int().nonnegative(),
  }),
  // Running timer card. `null` = no timer; populated when the user
  // has one running. `elapsedMinutes` is server-computed against the
  // server clock so the frontend can render at the same boundary.
  activeTimer: z
    .object({
      taskId: z.string().nullable(),
      taskName: z.string().nullable(),
      startAt: z.string().nullable(),
      elapsedMinutes: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  // Today's daily-report status. `isRequired` mirrors the reminder
  // pipeline — only `role: 'user'` accounts are nagged for dailies.
  dailyReportStatus: z
    .object({
      submitted: z.boolean(),
      submittedAt: z.string().nullable(),
      isRequired: z.boolean(),
      // Schedule-aware "a daily is actually due today" flag (false on
      // weekends / non-active days). Defaults TRUE so an older server
      // without the field keeps the pre-schedule-aware nag behaviour.
      dueToday: z.boolean().optional().default(true),
      // Most recent scheduled day before today + whether its daily was
      // filed — drives the "yesterday's daily is still open" highlight.
      previousDay: z
        .object({ date: z.string(), submitted: z.boolean() })
        .nullable()
        .optional()
        .default(null),
    })
    .optional(),
  // Weekly / monthly report prompts for the Daily Report card — present
  // for `role: 'user'` accounts, `null` per cadence when disabled.
  reportReminders: z
    .object({
      weekly: z
        .object({
          dueToday: z.boolean(),
          pastDue: z.boolean(),
          submitted: z.boolean(),
          periodStart: z.string(),
          periodEnd: z.string(),
          dueDayOfWeek: z.number().int(),
        })
        .nullable(),
      monthly: z
        .object({
          dueToday: z.boolean(),
          pastDue: z.boolean(),
          submitted: z.boolean(),
          periodStart: z.string(),
          periodEnd: z.string(),
          dueDayOfMonth: z.number().int(),
        })
        .nullable(),
    })
    .nullable()
    .optional()
    .default(null),
  // SOP step assignments (up to 5). TODO: the Sop schema has no
  // per-step row yet — populated today from the user's SOP-sourced
  // tasks so the frontend has something to render until the proper
  // step entity lands.
  sopSteps: z
    .array(
      z.object({
        stepId: z.string(),
        sopName: z.string(),
        stepTitle: z.string(),
        taskId: z.string().nullable(),
        taskName: z.string().nullable(),
        status: z.enum(['pending', 'in_progress', 'done']),
        dueDate: z.string().nullable(),
      }),
    )
    .optional(),
})

// ---------- Engagement modules (Sections 7, 8, 11, 12, 16, 17) ----------

export const notification = z.object({
  id: z.string().uuid(),
  /** Null for platform-level notifications (e.g. a support-ticket reply
   *  shown in a Super Admin's bell — Super Admins have no tenant). */
  tenantId: z.string().uuid().nullable(),
  userId: z.string().uuid(),
  /** Sender — null on system events with no human actor. */
  createdById: z.string().uuid().nullable().optional(),
  /** Resolved sender identity, joined server-side from `createdById`.
   *  All three fields are null when the row is a genuine system event
   *  (badge awarded, report reminder). */
  createdByName: z.string().nullable().optional(),
  createdByEmail: z.string().nullable().optional(),
  createdByAvatarUrl: z.string().nullable().optional(),
  kind: z.string(),
  title: z.string(),
  body: z.string().nullable().optional(),
  link: z.string().nullable().optional(),
  /** Focus target for click-to-highlight — e.g. 'task' + a taskId. Lets the
   *  bell repair the highlight deep-link independent of the `link` string. */
  resourceType: z.string().nullable().optional(),
  resourceId: z.string().nullable().optional(),
  readAt: z.string().nullable().optional(),
  createdAt: z.string(),
})

export const notificationsResponse = z.object({
  data: z.array(notification),
  unreadCount: z.number().int().nonnegative(),
  // Distinct notification kinds the user actually has — drives the
  // type-filter dropdown so it never offers a type that matches nothing.
  availableKinds: z.array(z.string()).optional().default([]),
  // Pagination metadata — added when the full Notifications page
  // (ProcessPro-style) was introduced. Optional so older callers
  // (the header bell with `limit=25`) still parse cleanly when
  // they don't ask for paginated data.
  pagination: z
    .object({
      page: z.number().int().min(1),
      limit: z.number().int().min(1),
      total: z.number().int().nonnegative(),
      hasMore: z.boolean(),
    })
    .optional(),
})

/** Tiny payload for the bell badge / sidebar — separate endpoint so
 *  the polling fetchers don't pay for the join + pagination cost of
 *  the full list. */
export const unreadCountResponse = z.object({
  count: z.number().int().nonnegative(),
})

/** Sent-box row — notifications I originated, joined to the recipient.
 *  No unreadCount because Sent items don't have an unread/read state from
 *  the sender's perspective. */
export const sentNotification = notification.extend({
  recipientFullName: z.string(),
  recipientEmailId: z.string().email(),
})

export const sentNotificationsResponse = z.object({
  data: z.array(sentNotification),
})

export const taskComment = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  authorId: z.string().uuid(),
  authorFullName: z.string(),
  authorEmailId: z.string().email(),
  authorAvatarUrl: z.string().nullable().optional(),
  body: z.string(),
  mentions: z.array(z.string().uuid()).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const taskCommentsResponse = z.object({
  data: z.array(taskComment),
})

export const taskHistoryEntry = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  eventType: z.string(),
  payload: z.unknown(),
  createdAt: z.string(),
  actorId: z.string().uuid().nullable().optional(),
  actorFullName: z.string().nullable().optional(),
  actorEmailId: z.string().email().nullable().optional(),
})

export const taskHistoryResponse = z.object({
  data: z.array(taskHistoryEntry),
})

export const approvalStatus = z.enum([
  'none',
  'pending',
  'approved',
  'rejected',
])

// ---------- Me (User-panel personal endpoints) ----------
//
// Defined here (rather than near the top of the file) because they
// reference `approvalStatus` and `task` — both of which need to be in
// scope first. The `// Me` marker further up explains why they're not
// where you might expect.

export const myTask = task.extend({
  listName: z.string(),
  spaceId: z.string().uuid(),
  spaceName: z.string(),
  workspaceId: z.string().uuid(),
  approvalStatus: approvalStatus,
  createdById: z.string().uuid(),
  /** Resolved assignee details (id + display name + avatar), parallel to
   *  `assigneeIds`. Powers the Assignee column + the assignee filter's
   *  real names. Optional so older API responses still parse — the UI
   *  falls back to a short id when absent. */
  assignees: z
    .array(
      z.object({
        userId: z.string().uuid(),
        fullName: z.string().nullable().optional(),
        avatarUrl: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
})
export const myTasksResponse = z.object({ data: z.array(myTask) })

export const workspaceOverviewResponse = z.object({
  // Scope this rollup covers — 'company' for a Company Admin (workspace-wide
  // feed), 'department' for a Manager/Team-Leader department rollup. Powers the
  // "Company-wide" vs "Your department" indicator. Optional/defaulted so older
  // responses still parse.
  scope: z.enum(['company', 'department']).optional().default('department'),
  workspaceCount: z.number().int(),
  members: z.object({
    total: z.number().int(),
    // Head-count EXCLUDING the viewing manager — lets the dashboard tell an
    // empty team apart from a fully-compliant one. Optional/defaulted so
    // older responses still parse.
    supervisedCount: z.number().int().nonnegative().default(0),
    // Breakdown by Work Role (operations-driven model), matching the Work Role
    // Management page and ordered by the admin-arranged hierarchy. A user with
    // no Work Role linked is counted under their level (e.g. "Employee"), as on
    // the Members page (server: utils/work-role-breakdown.js).
    byWorkRole: z
      .array(z.object({ name: z.string(), count: z.number().int() }))
      .optional()
      .default([]),
  }),
  spaces: z.object({
    total: z.number().int(),
    list: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        workspaceId: z.string().uuid(),
      }),
    ),
  }),
  tasks: z.object({
    total: z.number().int(),
    // Open = total minus approved (terminal). Powers the Manager
    // dashboard's "Team Tasks Open" hero KPI tile so the count
    // mirrors what the Team Tasks list shows when filtered to open.
    openCount: z.number().int().default(0),
    // All-time completed in scope — powers the "Completed Tasks" hero
    // tile (matches the Company Admin card's total-completions figure).
    completedCount: z.number().int().default(0),
    // Completions this week + last week — retained for period views.
    completedThisWeek: z.number().int().default(0),
    completedLastWeek: z.number().int().default(0),
    // Overdue team tasks — powers the "Overdue Across Team" action
    // card on the Manager dashboard.
    overdueCount: z.number().int().default(0),
    byApproval: z.object({
      none: z.number().int(),
      pending: z.number().int(),
      approved: z.number().int(),
      rejected: z.number().int(),
    }),
    // Workflow-status breakdown (TO DO / IN PROGRESS / COMPLETE /
    // tenant-defined values). Drives the Manager Dashboard donut.
    // Sorted descending by count on the backend so the largest
    // segment renders first.
    //
    // `color` is the hex colour the Company Admin set for that
    // status on the Workflows page. The donut renders it as-is so
    // a tenant's custom palette flows through end-to-end without
    // any client-side mapping. Falls back to a muted slate when
    // the status doesn't exist in the tenant's default workflow.
    byStatus: z
      .array(
        z.object({
          name: z.string(),
          count: z.number().int(),
          color: z.string().default('#94a3b8'),
        }),
      )
      .default([]),
    // Open tasks per Space — top 8 by count. Drives the
    // "Workload by Space" horizontal-bar chart on the Manager
    // dashboard. Names hydrated server-side so the frontend
    // doesn't need to do a second round-trip for the labels.
    bySpace: z
      .array(
        z.object({
          spaceId: z.string().uuid(),
          name: z.string(),
          count: z.number().int(),
        }),
      )
      .default([]),
    // [QA-20] Open team tasks in Spaces the viewer can't open — counted, never
    // named, so Workload by Space reconciles with the hero tiles.
    otherSpacesOpen: z.number().int().optional().default(0),
    // Non-repeat / Repeat Task Completion pies — the viewer's DEPARTMENT only
    // (server: the Team Tasks feed's department subset). `scope` is 'self'
    // when the viewer has no department view (pies = their own tasks).
    repeatSplit: repeatSplitSchema,
  }),
  pendingApprovals: z.array(
    z.object({
      taskId: z.string().uuid(),
      taskName: z.string(),
      spaceId: z.string().uuid(),
      listId: z.string().uuid(),
      requestedAt: z.string().nullable().optional(),
    }),
  ),
  // Top 5 overdue tasks across the team — surfaced as a preview list
  // inside the "Overdue Across Team" action card on the Manager
  // dashboard. Sorted oldest-due first so the most urgent items
  // come up top.
  overdueTopTasks: z
    .array(
      z.object({
        taskId: z.string().uuid(),
        taskName: z.string(),
        spaceId: z.string().uuid().nullable().optional(),
        listId: z.string().uuid(),
        dueDate: z.string().nullable().optional(),
      }),
    )
    .default([]),
  reportsLast30: z.number().int(),
  // Reports submitted this week — for the Manager dashboard's
  // "Reports Activity" section. `reportsLast30` is too wide for a
  // weekly cadence read.
  reportsThisWeek: z.number().int().default(0),
  timeThisWeekMinutes: z.number().int(),
  // Team minutes last week — drives the WoW delta chip on the
  // Manager dashboard's "Team Hours · This Week" hero tile.
  timeLastWeekMinutes: z.number().int().default(0),
  pointsLast30: z.number().int(),
  badgeCount: z.number().int(),
  topPerformersLast30: z.array(
    z.object({
      rank: z.number().int(),
      userId: z.string().uuid(),
      points: z.number().int(),
      fullName: z.string(),
      avatarUrl: z.string().nullable().optional(),
      departmentName: z.string().nullable().optional(),
    }),
  ),
  // ── Manager-only widgets ──────────────────────────────────────────
  // Top 10 assignees by open-task count, with per-user load /
  // overdue / weekly completion stats. Empty array for non-managers.
  tasksByAssignee: z
    .array(
      z.object({
        userId: z.string().uuid(),
        userName: z.string(),
        avatarUrl: z.string().nullable().optional(),
        openCount: z.number().int().nonnegative(),
        overdueCount: z.number().int().nonnegative(),
        completedThisWeek: z.number().int().nonnegative(),
      }),
    )
    .optional(),
  // Workload balance — overloaded vs idle relative to team mean.
  workloadBalance: z
    .object({
      overloaded: z.array(
        z.object({
          userId: z.string().uuid(),
          userName: z.string(),
          openCount: z.number().int().nonnegative(),
        }),
      ),
      idle: z.array(
        z.object({
          userId: z.string().uuid(),
          userName: z.string(),
          openCount: z.number().int().nonnegative(),
        }),
      ),
      mean: z.number(),
    })
    .optional(),
  // Blocked / stale tasks — proxy: open tasks last touched > 14d ago.
  blockedTasks: z.number().int().nonnegative().optional(),
  // SOP-sourced tasks still open across the team.
  sopTasksInProgress: z.number().int().nonnegative().optional(),
  // ── Team-leader-and-above widgets ─────────────────────────────────
  // Eligible employees who haven't submitted today's daily report.
  membersWithoutDailyReport: z
    .array(
      z.object({
        userId: z.string().uuid(),
        userName: z.string(),
        avatarUrl: z.string().nullable().optional(),
      }),
    )
    .optional(),
  // Members with zero activity today (no timer + no comment +
  // no report submitted).
  membersNoActivityToday: z
    .array(
      z.object({
        userId: z.string().uuid(),
        userName: z.string(),
        avatarUrl: z.string().nullable().optional(),
      }),
    )
    .optional(),
  // Count of tasks with approvalStatus='pending' across the team.
  tasksWaitingReview: z.number().int().nonnegative().optional(),
})

/** Per-member row for the Team Overview page (`GET /me/team`). One
 *  entry per active user in the actor's supervision scope, with their
 *  workspace + department placements and per-period stats joined in. */
export const teamMemberRow = z.object({
  userId: z.string().uuid(),
  fullName: z.string(),
  emailId: z.string().email(),
  avatarUrl: z.string().nullable().optional(),
  role: userRole,
  workRole: workRole,
  /** Resolved Work Role catalogue name (operations-driven — the truth for
   *  the Role chip). Null when the member has no custom Work Role; the UI
   *  then falls back to the tier label. */
  workRoleName: z.string().nullable().optional(),
  lastActiveAt: z.string().nullable().optional(),
  // Live presence — active within the last 15 minutes. Drives the roster's
  // online dot + the "Online now" tile. Optional so older responses parse.
  isOnline: z.boolean().optional().default(false),
  placements: z.array(
    z.object({
      workspaceId: z.string().uuid(),
      workspaceName: z.string(),
      departmentId: z.string().uuid().nullable().optional(),
      departmentName: z.string().nullable().optional(),
      role: z.enum(['admin', 'member', 'guest']),
      accessLevel: z.enum(['view', 'comment', 'edit', 'full']),
    }),
  ),
  stats: z.object({
    openTasks: z.number().int().nonnegative(),
    overdueTasks: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative().default(0),
    completedThisWeek: z.number().int().nonnegative(),
    reportsLast30: z.number().int().nonnegative(),
    pointsLast30: z.number().int(),
    timeThisWeekMinutes: z.number().int().nonnegative(),
  }),
})

export const teamResponse = z.object({
  data: z.array(teamMemberRow),
  // Distinct, feed-based headline totals (each task counted once) so the
  // page's "Open Tasks" / "Overdue Tasks" KPIs match the Team Tasks
  // page rather than the per-assignee column sum. Optional so older API
  // responses without the field still parse.
  summary: z
    .object({
      openTaskCount: z.number().int().nonnegative(),
      overdueTaskCount: z.number().int().nonnegative(),
      // Roster members active in the last 15 min — powers the "Online now" tile.
      onlineCount: z.number().int().nonnegative().default(0),
      // Content-free facts for every open TO DO / overdue task, so the KPI
      // cards can recompute DISTINCT counts for a filtered member subset
      // (a multi-assignee task is counted once). Optional for older API builds.
      openTaskFacts: z
        .array(
          z.object({
            assigneeIds: z.array(z.string()).default([]),
            todo: z.boolean(),
            overdue: z.boolean(),
          }),
        )
        .optional()
        .default([]),
    })
    .optional(),
})

export const approvalQueueRow = z.object({
  taskId: z.string().uuid(),
  taskName: z.string(),
  taskPriority: taskPriority.nullable().optional(),
  taskDueDate: z.string().nullable().optional(),
  approvalRequestedAt: z.string().nullable().optional(),
  approvalNote: z.string().nullable().optional(),
  /** Which level the task is currently waiting on. Drives the
   *  "Awaiting Team Leader / Manager / Company Admin" chip. */
  pendingReviewRole: z
    .enum(['team_leader', 'manager', 'company_admin'])
    .nullable()
    .optional(),
  listId: z.string().uuid(),
  spaceId: z.string().uuid(),
  spaceName: z.string(),
  workspaceId: z.string().uuid(),
  assignees: z.array(
    z.object({
      userId: z.string().uuid(),
      fullName: z.string(),
      emailId: z.string().email(),
      avatarUrl: z.string().nullable().optional(),
      departmentId: z.string().uuid().nullable().optional(),
    }),
  ),
})

export const approvalQueueResponse = z.object({
  data: z.array(approvalQueueRow),
})

export const timeEntry = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  taskId: z.string().uuid(),
  taskName: z.string().nullable().optional(),
  startAt: z.string(),
  endAt: z.string().nullable().optional(),
  durationMinutes: z.number().int().nullable().optional(),
  source: z.enum(['timer', 'manual', 'auto-status']),
  note: z.string().nullable().optional(),
  createdAt: z.string(),
})

export const timeEntriesResponse = z.object({
  data: z.array(timeEntry),
  totalMinutes: z.number().int().nonnegative(),
})

export const activeTimer = z.object({
  userId: z.string().uuid(),
  taskId: z.string().uuid(),
  taskName: z.string().nullable().optional(),
  listId: z.string().uuid().nullable().optional(),
  startAt: z.string(),
  note: z.string().nullable().optional(),
})

export const leaderboardRow = z.object({
  rank: z.number().int(),
  userId: z.string().uuid(),
  points: z.number().int(),
  fullName: z.string(),
  emailId: z.string().email(),
  avatarUrl: z.string().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  workRole: workRole.optional().default('employee'),
  // The assigned Work Role's catalogue NAME (e.g. a custom "Senior
  // Designer"). Preferred over the inert `workRole` tier slug for display.
  // Optional so older backends that don't send it still parse.
  workRoleName: z.string().nullable().optional(),
  // Count of DISTINCT tasks this member completed, split by the task's
  // priority. Optional so older backends that don't send it still parse.
  completedByPriority: z
    .object({
      urgent: z.number().int(),
      high: z.number().int(),
      normal: z.number().int(),
      low: z.number().int(),
    })
    .optional(),
})

export const leaderboardResponse = z.object({
  data: z.array(leaderboardRow),
  /** The viewer's TRUE rank + total players in their peer group, set by the
   *  backend only when their visible board collapsed to self-only (a member
   *  who can't see others). Null/absent otherwise — the client then derives
   *  rank/players from `data`, as before. Lets "My Rank"/"Players" reflect
   *  real standing (e.g. #2 of 7) even when the table shows only the viewer. */
  viewerRank: z.number().int().nullable().optional(),
  viewerPlayers: z.number().int().nullable().optional(),
})

export const pointsLedgerEntry = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  taskId: z.string().uuid().nullable().optional(),
  // Side-loaded by the backend so the UI can render the task name next
  // to each ledger row without a per-entry /tasks/:id lookup. Null when
  // the task was hard-deleted after the award (the points still stand).
  taskName: z.string().nullable().optional(),
  // Task dates side-loaded for the "My recent points" history: the task's
  // due date, its completion timestamp, and when this user was assigned.
  // Null when unset / task deleted / not applicable (e.g. report awards).
  taskDueDate: z.string().nullable().optional(),
  taskCompletedAt: z.string().nullable().optional(),
  taskAssignedAt: z.string().nullable().optional(),
  points: z.number().int(),
  reason: z.string(),
  awardedAt: z.string(),
})

export const myGamificationStats = z.object({
  total: z.number().int(),
  /** Consecutive active days (time logged OR task completed), today-
   *  or-yesterday anchored. NOT rendered anywhere — the Current Streak
   *  KPI tile was removed from the Leaderboard. Kept so the backend's
   *  existing payload still parses; optional for older payloads too. */
  currentStreakDays: z.number().int().optional(),
  recent: z.array(pointsLedgerEntry),
  badges: z.array(
    z.object({
      id: z.string().uuid(),
      tenantId: z.string().uuid(),
      userId: z.string().uuid(),
      badgeCode: z.string(),
      awardedAt: z.string(),
    }),
  ),
  /** Distinct delivered tasks credited to the user — the metric badges are
   *  earned on. Powers the "X / N tasks" progress toward locked badges.
   *  Optional/default so older API payloads still parse. */
  creditedTaskCount: z.number().int().optional().default(0),
  /** The tenant's active badge catalogue (built-in defaults when none
   *  defined) so the Badges modal can list earned + locked badges with
   *  their thresholds. Optional/default for older payloads. */
  badgeCatalogue: z
    .array(
      z.object({
        code: z.string(),
        name: z.string(),
        description: z.string().optional().default(''),
        requiredTaskCount: z.number().int(),
        sortOrder: z.number().optional().default(0),
      }),
    )
    .optional()
    .default([]),
  /** The tenant's EFFECTIVE scoring config, so the Leaderboard's
   *  "how points work" cards show this organisation's actual numbers
   *  (e.g. an admin-tuned Urgent = 40) rather than hardcoded defaults.
   *  Optional so older API payloads still parse. */
  settings: z
    .object({
      priorityPoints: z.object({
        urgent: z.number(),
        high: z.number(),
        normal: z.number(),
        low: z.number(),
      }),
      timeMultipliers: z.object({
        early: z.number(),
        onTime: z.number(),
        slightlyLate: z.number(),
        veryLate: z.number(),
      }),
      reportPoints: z
        .object({
          daily: z.number(),
          weekly: z.number(),
          monthly: z.number(),
        })
        .optional(),
    })
    .optional(),
})

// ---------- SOP library (external project) + task breakdown ----------

export const sopLibraryEntry = z.object({
  id: z.string(),
  // Slug from the upstream catalogue. Preferred over `id` when calling
  // the detail endpoint because SOP-Galaxy-style APIs route details by
  // slug. Falls back to `id` when the upstream omits it.
  slug: z.string().nullable().optional(),
  title: z.string(),
  summary: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  // Department info stamped by the backend normaliser when the upstream
  // catalogue groups SOPs by department (SOP Galaxy / ProcessPro shape).
  // Optional so a flat upstream that doesn't carry departments still
  // parses — those SOPs fall into a single "Uncategorised" bucket on
  // the page.
  departmentId: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  departmentColor: z.string().nullable().optional(),
  // Extended catalogue fields — surfaced in the detail dialog so the
  // page can render rich SOP metadata WITHOUT a second backend
  // round-trip (the upstream detail endpoint was unreliable and the
  // catalogue already carries everything below).
  category: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  isCritical: z.boolean().nullable().optional(),
  version: z.number().nullable().optional(),
  versionLabel: z.string().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
  implementedAt: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  owner: z
    .object({
      name: z.string().nullable().optional(),
      email: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  tags: z.array(z.string()).optional(),
  complianceMappings: z.array(z.string()).optional(),
  // Full SOP content, when the upstream catalogue ships it inline
  // (`include_content: true`). Two known shapes:
  //   - `{ structured: { type: 'text', content: '...md' }, plain_text, html }`
  //   - `{ structured: { type: 'structured', title, header, sections, customSectionDefs }, plain_text, html }`
  // Schema kept intentionally loose (`unknown`) because the
  // structured shape has a deeply nested, variable-key sections
  // object — strict zod here would reject perfectly valid responses
  // any time the upstream adds a new section. The renderer in
  // `SopsPage.jsx` is defensive about every field.
  content: z.unknown().nullable().optional(),
})

export const sopLibraryResponse = z.object({
  data: z.array(sopLibraryEntry),
  // `connected` + `error` were added when the SOPs Library page was
  // re-pointed at the external SOP Galaxy integration. The backend now
  // returns a clean disconnected payload (`{ data: [], connected: false,
  // error?: '…' }`) when the tenant hasn't configured the integration
  // OR when the upstream endpoint fails. Both are optional so older
  // clients that don't read them keep parsing the response.
  connected: z.boolean().optional(),
  error: z.string().optional(),
})

/* ---------- Tenant-wide SOP library (locally-uploaded) ---------- */

export const tenantSop = z.object({
  id: z.string().uuid(),
  title: z.string(),
  summary: z.string().nullable().optional(),
  steps: z.number().int(),
  originalFilename: z.string().nullable().optional(),
  listId: z.string().uuid(),
  listName: z.string().nullable().optional(),
  spaceId: z.string().uuid().nullable().optional(),
  spaceName: z.string().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  workspaceId: z.string().uuid().nullable().optional(),
  workspaceName: z.string().nullable().optional(),
  ownerId: z.string().uuid(),
  ownerName: z.string(),
  createdAt: z.string().or(z.date()).nullable().optional(),
})

export const tenantSopsResponse = z.object({
  data: z.array(tenantSop),
  counts: z.object({
    total: z.number().int(),
    workspaces: z.number().int(),
  }),
})

/* ---------- Global search ---------- */

export const searchHit = z.object({
  id: z.string(),
  type: z.enum(['task', 'comment', 'sop', 'doc', 'space', 'list', 'folder']),
  title: z.string(),
  meta: z.record(z.string(), z.any()).optional(),
  score: z.number().optional(),
})

export const searchResponse = z.object({
  q: z.string(),
  counts: z.object({
    task: z.number().int(),
    comment: z.number().int(),
    sop: z.number().int(),
    doc: z.number().int(),
    space: z.number().int(),
    list: z.number().int(),
    folder: z.number().int(),
  }),
  results: z.object({
    task: z.array(searchHit),
    comment: z.array(searchHit),
    sop: z.array(searchHit),
    doc: z.array(searchHit),
    space: z.array(searchHit),
    list: z.array(searchHit),
    folder: z.array(searchHit),
  }),
})

/* ---------- Tenant audit log ---------- */

export const auditLogEntry = z.object({
  id: z.string().uuid(),
  actorId: z.string().uuid().nullable(),
  actorName: z.string(),
  actorEmail: z.string().nullable().optional(),
  actorRole: z.string().nullable(),
  action: z.string(),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  metadata: z.record(z.string(), z.any()),
  ipAddress: z.string().nullable(),
  createdAt: z.string().or(z.date()),
})

export const auditLogResponse = z.object({
  data: z.array(auditLogEntry),
  pagination: paginationMeta.optional(),
})

/** Flat list of subtask candidates produced by either the SOP library or
 *  a one-shot upload. The user can edit the list before committing it. */
export const subtaskCandidate = z.object({
  name: z.string(),
  description: z.string().optional().default(''),
})

export const breakdownPreview = z.object({
  parentTaskId: z.string().uuid(),
  parentTaskName: z.string(),
  sopId: z.string().nullable(),
  sopTitle: z.string(),
  // 'text' = paste-text source. Backend distinguishes the three for
  // analytics + audit; the UI just shows where the SOP came from.
  source: z.enum(['library', 'upload', 'text']),
  subtasks: z.array(subtaskCandidate),
  provenance: z
    .object({
      provider: z.string(),
      model: z.string().nullable().optional(),
    })
    .optional(),
})

export const bulkSubtasksResponse = z.object({
  message: z.string(),
  taskIds: z.array(z.string().uuid()),
})

/* ---------- Workflows ---------- */

export const workflowStateCategory = z.enum([
  'backlog',
  'active',
  'review',
  'done',
  'cancelled',
])

export const workflowState = z.object({
  key: z.string(),
  label: z.string(),
  color: z.string().nullable().optional(),
  category: workflowStateCategory,
  // Active states show in Spaces; inactive ones are hidden. The last
  // active state (by order) is the workflow's completion state.
  active: z.boolean().optional().default(true),
  order: z.number(),
  wipLimit: z.number().nullable().optional(),
  // How many tasks currently sit on this status (list endpoint only) — lets
  // the editor block a delete-click while the status is still in use.
  taskCount: z.number().int().optional().default(0),
})

export const workflowTransition = z.object({
  from: z.string(),
  to: z.string(),
  requiresApproval: z.boolean().optional().default(false),
  requiresComment: z.boolean().optional().default(false),
})

/** Department summary attached to each Workflow on the list endpoint
 *  so the Workflows page can show "Used in: A, B, C" without an extra
 *  round-trip per row. (Workflow assignment moved from workspace →
 *  department.) */
export const workflowDepartmentUsage = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

export const workflow = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  isDefault: z.boolean(),
  states: z.array(workflowState),
  transitions: z.array(workflowTransition),
  departmentUsage: z.array(workflowDepartmentUsage).optional().default([]),
  createdAt: z.string().or(z.date()).nullable().optional(),
  updatedAt: z.string().or(z.date()).nullable().optional(),
})

export const workflowsResponse = z.object({ data: z.array(workflow) })

/* ---------- Projects ---------- */

export const projectMode = z.enum(['sop', 'traditional', 'hybrid'])

export const projectStatus = z.enum([
  'planning',
  'active',
  'on_hold',
  'completed',
  'archived',
])

export const project = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  spaceId: z.string().uuid(),
  departmentId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  mode: projectMode,
  workflowId: z.string().uuid().nullable().optional(),
  ownerId: z.string().uuid().nullable().optional(),
  color: z.string().nullable().optional(),
  startDate: z.string().or(z.date()).nullable().optional(),
  endDate: z.string().or(z.date()).nullable().optional(),
  status: projectStatus,
  isDefault: z.boolean(),
  archivedAt: z.string().or(z.date()).nullable().optional(),
  createdAt: z.string().or(z.date()).nullable().optional(),
  updatedAt: z.string().or(z.date()).nullable().optional(),
})

export const projectsResponse = z.object({ data: z.array(project) })

/* ---------- Task dependencies ---------- */

export const dependencyType = z.enum(['FS', 'SS', 'FF', 'SF'])

export const taskDependency = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  fromTaskId: z.string().uuid(),
  toTaskId: z.string().uuid(),
  type: dependencyType,
  lagDays: z.number(),
  createdAt: z.string().or(z.date()).nullable().optional(),
})

export const taskDependenciesResponse = z.object({
  blockedBy: z.array(taskDependency),
  blocking: z.array(taskDependency),
})

/* ---------- Labels ---------- */

export const label = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  workspaceId: z.string().uuid().nullable(),
  name: z.string(),
  color: z.string(),
  scope: z.enum(['workspace', 'tenant']),
  createdAt: z.string().or(z.date()).nullable().optional(),
  updatedAt: z.string().or(z.date()).nullable().optional(),
})

export const labelsResponse = z.object({ data: z.array(label) })

/* ---------- Board / Calendar view payloads ---------- */

export const boardTask = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  priority: z.string().nullable().optional(),
  startDate: z.string().or(z.date()).nullable().optional(),
  dueDate: z.string().or(z.date()).nullable().optional(),
  startDateHasTime: z.boolean().optional(),
  dueDateHasTime: z.boolean().optional(),
  dueEndOfDay: z.boolean().optional(),
  completedAt: z.string().or(z.date()).nullable().optional(),
  labelIds: z.array(z.string()),
  assigneeIds: z.array(z.string()),
  parentTaskId: z.string().nullable().optional(),
  createdAt: z.string().or(z.date()).nullable().optional(),
})

export const boardColumn = z.object({
  key: z.string(),
  label: z.string(),
  color: z.string().nullable().optional(),
  category: z.string().optional(),
  wipLimit: z.number().nullable().optional(),
  tasks: z.array(boardTask),
})

export const boardResponse = z.object({
  workflow: z.object({
    id: z.string().uuid(),
    name: z.string(),
    isDefault: z.boolean(),
    states: z.array(workflowState),
    transitions: z.array(workflowTransition),
  }),
  columns: z.array(boardColumn),
  groupBy: z.string(),
})

export const calendarTask = z.object({
  id: z.string().uuid(),
  listId: z.string().uuid(),
  name: z.string(),
  status: z.string(),
  priority: z.string().nullable().optional(),
  startDate: z.string().or(z.date()).nullable().optional(),
  dueDate: z.string().or(z.date()).nullable().optional(),
  startDateHasTime: z.boolean().optional(),
  dueDateHasTime: z.boolean().optional(),
  dueEndOfDay: z.boolean().optional(),
  labelIds: z.array(z.string()),
})

export const calendarTasksResponse = z.object({ data: z.array(calendarTask) })

// ---------- Platform Admin: Email Templates ----------

export const emailTemplate = z.object({
  id: z.string().uuid(),
  name: z.string(),
  // Stable identifier the backend looks up when a system event fires
  // (user_registration, forgot_password, …). Unique per platform.
  templateKey: z.string(),
  subject: z.string(),
  bodyHtml: z.string(),
  // Placeholder names (without braces) the template expects, e.g.
  // ["name", "otp"]. Older responses may omit it — default to [].
  variables: z.array(z.string()).optional().default([]),
  // Labels of the system events this template is assigned to — drives
  // the "Assigned Event" column. Server-computed; absent on older rows.
  assignedEvents: z.array(z.string()).optional().default([]),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByName: z.string().nullable().optional(),
})
export const emailTemplatesResponse = z.object({
  data: z.array(emailTemplate),
})

export const emailLogStatusEnum = z.enum([
  'sent',
  // `delivered` (provider-confirmed) and `queued` (sitting in the email
  // queue) are webhook/queue-driven states the model can write; the
  // enum must list them or a row in either state would fail to parse
  // and break the whole Sent Emails tab.
  'delivered',
  'failed',
  'pending',
  'queued',
  // `bounced` is a hard bounce (webhook-driven). SMTP-only deploys may
  // never see it, but the enum carries it so the row parses if they do.
  'bounced',
  // `skipped` is written when an event handler calls `sendByEvent` but
  // there's no active template/assignment to send. Surfacing it in the
  // Logs tab is the only way the admin can see WHY an expected email
  // (welcome, password reset, …) never went out.
  'skipped',
])
export const emailLog = z.object({
  id: z.string().uuid(),
  recipient: z.string(),
  recipientName: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  templateKey: z.string().nullable().optional(),
  templateName: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  emailType: z.string().nullable().optional(),
  status: emailLogStatusEnum,
  error: z.string().nullable().optional(),
  sentAt: z.string().nullable().optional(),
  createdAt: z.string(),
})
export const emailLogsResponse = z.object({
  data: z.array(emailLog),
  pagination: paginationMeta,
})
export const emailLogCountsTabResponse = z.object({
  counts: z.object({
    total: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
  }),
})

/** Single log row WITH the rendered body — returned by
 *  `GET /super-admin/email-logs/:id` and consumed by the Sent Emails
 *  preview dialog. The list response above strips html/text to keep
 *  the payload light; this detail shape hydrates them on demand. */
export const emailLogDetail = emailLog.extend({
  html: z.string().nullable().optional(),
  text: z.string().nullable().optional(),
})

// Email event → template assignments
export const emailAssignmentEvent = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  variables: z.array(z.string()).optional().default([]),
  templateId: z.string().nullable().optional(),
  templateName: z.string().nullable().optional(),
  isActive: z.boolean(),
})
export const emailAssignmentsResponse = z.object({
  groups: z.array(
    z.object({
      category: z.string(),
      events: z.array(emailAssignmentEvent),
    }),
  ),
})

// Event catalogue (with the variables each event provides) — used by
// the template dialog's dynamic-variable picker.
export const emailEventsResponse = z.object({
  groups: z.array(
    z.object({
      category: z.string(),
      events: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          description: z.string(),
          variables: z.array(z.string()).optional().default([]),
        }),
      ),
    }),
  ),
})

// SMTP settings (singleton)
export const emailSettings = z.object({
  // Kept as a permissive string (not an enum) so a provider added on the
  // backend later isn't silently stripped out of the parsed response.
  provider: z.string().optional().default('smtp'),
  host: z.string().optional().default(''),
  port: z.number().optional().default(587),
  encryption: z.enum(['tls', 'ssl', 'none']).optional().default('tls'),
  authMode: z.enum(['login', 'none']).optional().default('login'),
  username: z.string().optional().default(''),
  hasPassword: z.boolean().optional().default(false),
  fromName: z.string().optional().default(''),
  fromEmail: z.string().optional().default(''),
  replyTo: z.string().optional().default(''),
  // Comma-separated allowlist of sender domains authenticated with the mail
  // provider. Blank = fall back to the server's AUTHENTICATED_EMAIL_DOMAINS env.
  authenticatedDomains: z.string().optional().default(''),
  updatedAt: z.string().nullable().optional(),
})

// One account in the multi-SMTP failover pool. Password is never returned —
// `hasPassword` tells the form whether to show "leave blank to keep current".
export const smtpAccount = z.object({
  id: z.string(),
  label: z.string().optional().default(''),
  host: z.string().optional().default(''),
  port: z.number().optional().default(587),
  encryption: z.enum(['tls', 'ssl', 'none']).optional().default('tls'),
  authMode: z.enum(['login', 'none']).optional().default('login'),
  username: z.string().optional().default(''),
  hasPassword: z.boolean().optional().default(false),
  senderName: z.string().optional().default(''),
  senderEmail: z.string().optional().default(''),
  priority: z.number().optional().default(100),
  isPrimary: z.boolean().optional().default(false),
  enabled: z.boolean().optional().default(true),
  blocked: z.boolean().optional().default(false),
  status: z.string().optional().default('active'),
  consecutiveFailures: z.number().optional().default(0),
  lastError: z.string().nullable().optional(),
  lastFailureAt: z.string().nullable().optional(),
  lastSuccessAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
})
export const smtpAccountList = z.array(smtpAccount)

// Session / token lifetime settings — the effective values (DB → env →
// default) that drive login + refresh token expiry.
export const sessionSettings = z.object({
  accessTokenExpiryMinutes: z.number().optional().default(15),
  refreshTokenExpiryDays: z.number().optional().default(7),
  updatedAt: z.string().nullable().optional(),
})

// Razorpay payment-gateway settings. The two secrets are never sent to
// the browser — `hasKeySecret` / `hasWebhookSecret` only indicate
// whether a value is stored.
export const paymentSettings = z.object({
  razorpayKeyId: z.string().optional().default(''),
  mode: z.enum(['test', 'live']).optional().default('test'),
  enabled: z.boolean().optional().default(false),
  hasKeySecret: z.boolean().optional().default(false),
  hasWebhookSecret: z.boolean().optional().default(false),
  updatedAt: z.string().nullable().optional(),
})

// One payment transaction row.
export const transaction = z.object({
  id: z.string(),
  tenantId: z.string().nullable().optional(),
  tenantName: z.string().nullable().optional(),
  planCode: z.string().optional().default(''),
  planName: z.string().nullable().optional(),
  cadence: z.string().optional().default('monthly'),
  amount: z.number().optional().default(0),
  currency: z.string().optional().default('INR'),
  razorpayOrderId: z.string().nullable().optional(),
  razorpayPaymentId: z.string().nullable().optional(),
  status: z.string().optional().default('created'),
  mode: z.string().optional().default('test'),
  createdAt: z.string(),
})

export const transactionsResponse = z.object({
  data: z.array(transaction),
  pagination: paginationMeta.optional(),
  summary: z.object({
    totalRevenue: z.number().optional().default(0),
    pendingCount: z.number().optional().default(0),
    successfulCount: z.number().optional().default(0),
    // Count of Razorpay transactions in the `failed` state — drives
    // the 4th KPI card on the Payments page. Optional with a 0
    // default so older API responses (pre-failedCount rollout)
    // still parse cleanly.
    failedCount: z.number().optional().default(0),
    currency: z.string().optional().default('INR'),
  }),
})

// ---------- Invoices (Company Admin own + Super Admin all) ----------
export const invoiceRow = z
  .object({
    _id: z.string(),
    invoiceNumber: z.string(),
    tenantId: z.string().nullable().optional(),
    tenantName: z.string().optional().default('—'),
    userId: z.string().nullable().optional(),
    transactionId: z.string().nullable().optional(),
    planCode: z.string().nullable().optional(),
    planName: z.string().nullable().optional(),
    cadence: z.string().optional().default('monthly'),
    currency: z.string().optional().default('INR'),
    amount: z.number().optional().default(0),
    tax: z.number().optional().default(0),
    total: z.number().optional().default(0),
    originalAmount: z.number().nullable().optional(),
    discountAmount: z.number().optional().default(0),
    couponCode: z.string().nullable().optional(),
    gateway: z.string().nullable().optional(),
    paymentRef: z.string().nullable().optional(),
    billToName: z.string().optional().default(''),
    billToEmail: z.string().optional().default(''),
    status: z.string().optional().default('paid'),
    issuedAt: z.string(),
  })
  .passthrough()

export const invoicesResponse = z.object({
  invoices: z.array(invoiceRow),
  pagination: z
    .object({
      page: z.number().int(),
      limit: z.number().int(),
      total: z.number().int(),
      totalPages: z.number().int(),
    })
    .passthrough(),
})

// ---------- Super Admin: Visitor Logs ----------
// `.passthrough()` on the nested objects so an added backend field is
// never silently dropped before the analytics UI can read it.
const visitorGeo = z
  .object({
    country: z.string().optional().default(''),
    region: z.string().optional().default(''),
    city: z.string().optional().default(''),
  })
  .passthrough()

export const visitorLogRow = z
  .object({
    _id: z.string(),
    createdAt: z.string(),
    userId: z.string().nullable().optional(),
    userName: z.string().optional().default(''),
    userEmail: z.string().optional().default(''),
    role: z.string().optional().default('user'),
    tenantId: z.string().nullable().optional(),
    tenantName: z.string().optional().default('Platform'),
    sessionId: z.string().optional().default(''),
    eventType: z.string().optional().default('page_view'),
    path: z.string().optional().default(''),
    pageTitle: z.string().optional().default(''),
    referrer: z.string().optional().default(''),
    durationMs: z.number().nullable().optional(),
    ip: z.string().optional().default(''),
    geo: visitorGeo.optional(),
    userAgent: z.string().optional().default(''),
    browser: z.string().optional().default(''),
    os: z.string().optional().default(''),
    device: z.string().optional().default('unknown'),
  })
  .passthrough()

const visitorPagination = z
  .object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
  })
  .passthrough()

export const visitorLogsResponse = z.object({
  logs: z.array(visitorLogRow),
  pagination: visitorPagination,
})

export const visitorSessionRow = z
  .object({
    sessionId: z.string(),
    userId: z.string().nullable().optional(),
    userName: z.string().optional().default(''),
    userEmail: z.string().optional().default(''),
    role: z.string().optional().default('user'),
    tenantId: z.string().nullable().optional(),
    tenantName: z.string().optional().default('Platform'),
    device: z.string().optional().default('unknown'),
    browser: z.string().optional().default(''),
    os: z.string().optional().default(''),
    ip: z.string().optional().default(''),
    country: z.string().nullable().optional(),
    startedAt: z.string(),
    endedAt: z.string(),
    entryPage: z.string().optional().default(''),
    exitPage: z.string().optional().default(''),
    eventCount: z.number().int().optional().default(0),
    pageViewCount: z.number().int().optional().default(0),
    durationMs: z.number().nullable().optional(),
  })
  .passthrough()

export const visitorSessionsResponse = z.object({
  sessions: z.array(visitorSessionRow),
  pagination: visitorPagination,
})

const countRow = (key) =>
  z.object({ [key]: z.string(), count: z.number().int() }).passthrough()

export const visitorStatsResponse = z
  .object({
    totals: z
      .object({
        totalEvents: z.number().int(),
        pageViews: z.number().int(),
        /** Page views carrying a non-blank sessionId — the numerator
         *  for "Average Pages / Session", matched to the population
         *  `uniqueSessions` counts. Optional for older API payloads
         *  (the page falls back to raw pageViews). */
        sessionPageViews: z.number().int().optional(),
        uniqueUsers: z.number().int(),
        uniqueSessions: z.number().int(),
        activeSessions: z.number().int(),
      })
      .passthrough(),
    byRole: z.array(countRow('role')),
    byDevice: z.array(countRow('device')),
    topBrowsers: z.array(countRow('browser')),
    topCountries: z.array(countRow('country')),
    topPages: z.array(countRow('path')),
    byOrganization: z.array(
      z
        .object({
          tenantId: z.string().nullable().optional(),
          tenantName: z.string(),
          count: z.number().int(),
        })
        .passthrough(),
    ),
    eventsPerDay: z.array(
      z.object({ date: z.string(), count: z.number().int() }).passthrough(),
    ),
  })
  .passthrough()

export const visitorPageAnalyticsResponse = z.object({
  pages: z.array(
    z
      .object({
        path: z.string(),
        views: z.number().int(),
        uniqueUsers: z.number().int(),
        uniqueSessions: z.number().int(),
        avgTimeMs: z.number().nullable().optional(),
        lastVisited: z.string().nullable().optional(),
      })
      .passthrough(),
  ),
  pagination: visitorPagination,
})

const visitorUser = z
  .object({
    name: z.string().optional().default(''),
    email: z.string().optional().default(''),
    role: z.string().optional().default(''),
  })
  .passthrough()

export const visitorSessionDetailResponse = z.object({
  summary: z
    .object({
      sessionId: z.string(),
      user: visitorUser,
      tenantName: z.string().optional().default('Platform'),
      startedAt: z.string(),
      endedAt: z.string(),
      durationMs: z.number().nullable().optional(),
      pageViewCount: z.number().int(),
      entryPage: z.string().optional().default(''),
      exitPage: z.string().optional().default(''),
      device: z.string().optional().default(''),
      browser: z.string().optional().default(''),
      os: z.string().optional().default(''),
      ip: z.string().optional().default(''),
      country: z.string().optional().default(''),
    })
    .passthrough(),
  events: z.array(visitorLogRow),
})

export const visitorJourneyResponse = z.object({
  user: visitorUser.nullable(),
  sessionCount: z.number().int(),
  eventCount: z.number().int(),
  sessions: z.array(
    z
      .object({
        sessionId: z.string(),
        startedAt: z.string(),
        endedAt: z.string(),
        pageViewCount: z.number().int(),
        events: z.array(visitorLogRow),
      })
      .passthrough(),
  ),
})

export const visitorExportResponse = z.object({
  rows: z.array(z.record(z.string(), z.any())),
  truncated: z.boolean().optional().default(false),
})

export const visitorRetentionResponse = z.object({
  retentionDays: z.number().int(),
  lastPurgeAt: z.string().nullable().optional(),
})

// ── Referral program ─────────────────────────────────────────────────
// `status` is z.string() (not z.enum) so a new backend value can never throw
// the whole parse — the pages map known values and fall through gracefully.
// Reward = a free plan extension (days + resulting renewal date); null until
// a referral is 'rewarded'.
export const referralConfig = z.object({
  enabled: z.boolean(),
  extensionDays: z.number().int(),
})

const referralReward = z
  .object({
    days: z.number().int(),
    until: z.string().nullable(),
  })
  .nullable()

const referralStats = z.object({
  pending: z.number().int().nonnegative().default(0),
  qualified: z.number().int().nonnegative().default(0),
  rewarded: z.number().int().nonnegative().default(0),
  void: z.number().int().nonnegative().default(0),
})

export const referralMeResponse = z.object({
  code: z.string(),
  link: z.string(),
  config: referralConfig,
  stats: referralStats,
  referrals: z.array(
    z.object({
      id: z.string(),
      company: z.string().nullable(),
      email: z.string().nullable(),
      status: z.string(), // pending | qualified | rewarded | void
      reward: referralReward,
      createdAt: z.string(),
    }),
  ),
})

export const referralFunnel = z.object({
  signups: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  qualified: z.number().int().nonnegative(),
  rewarded: z.number().int().nonnegative(),
  void: z.number().int().nonnegative(),
  conversionRate: z.number(),
})

export const referralListResponse = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      referrer: z.string(),
      referee: z.string(),
      refereeEmail: z.string().nullable(),
      status: z.string(),
      reward: referralReward,
      createdAt: z.string(),
      qualifiedAt: z.string().nullable(),
      rewardedAt: z.string().nullable(),
    }),
  ),
})


// ── "Our Products" catalogue ────────────────────────────────────────────────
// Lenient by design (z.string() not z.enum for status/access) so new
// server-side values never break parsing — see the schema-strips note.
export const product = z.object({
  id: z.string(),
  productKey: z.string(),
  name: z.string(),
  shortDescription: z.string().nullable().optional().default(''),
  tags: z.array(z.string()).optional().default([]),
  logoUrl: z.string().nullable().optional(),
  bannerUrl: z.string().nullable().optional(),
  gallery: z.array(z.string()).optional().default([]),
  // Admin-only stored keys (absent on the customer payload).
  logoKey: z.string().nullable().optional(),
  bannerKey: z.string().nullable().optional(),
  galleryKeys: z.array(z.string()).optional().default([]),
  websiteUrl: z.string().nullable().optional(),
  features: z.array(z.string()).optional().default([]),
  platforms: z.array(z.string()).optional().default([]),
  status: z.string().optional().default('active'),
  featured: z.boolean().optional().default(false),
  displayOrder: z.number().int().optional().default(0),
  isCurrentProduct: z.boolean().optional().default(false),
  // Customer payload only: 'owned' | 'buy' | 'coming_soon'.
  access: z.string().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
})

export const productListResponse = z.object({
  data: z.array(product),
  page: z.number().int().optional().default(1),
  limit: z.number().int().optional().default(20),
  total: z.number().int().optional().default(0),
  totalPages: z.number().int().optional().default(1),
})

export const customerProductsResponse = z.object({
  data: z.array(product),
})

export const productUploadResponse = z.object({
  key: z.string(),
  url: z.string().nullable().optional(),
})

// ── Employee-progress Analytics (platform + company) ───────────────────────
// Every field the /analytics + /super-admin/analytics endpoints return MUST be
// declared here — Zod strips undeclared keys, so an omitted field never reaches
// the page. New fields are optional/defaulted for backward-compat.
const analyticsKpis = z
  .object({
    companies: z.number().optional(),
    employees: z.number().optional().default(0),
    tasksTotal: z.number().optional().default(0),
    tasksCompleted: z.number().optional().default(0),
    tasksTodo: z.number().optional().default(0),
    tasksInProgress: z.number().optional().default(0),
    tasksOpen: z.number().optional().default(0),
    tasksOverdue: z.number().optional().default(0),
    completionRate: z.number().optional().default(0),
    goalsTotal: z.number().optional().default(0),
    goalsCompleted: z.number().optional().default(0),
    goalsActive: z.number().optional().default(0),
    reportsSubmitted: z.number().optional().default(0),
    verificationPending: z.number().optional().default(0),
    // Repeat (recurring) vs one-off task totals. tasksTotal = repeatedTotal +
    // normalTotal. Same field names the company/employee rows use.
    repeatedTotal: z.number().optional().default(0),
    normalTotal: z.number().optional().default(0),
  })
  .passthrough()

const analyticsBreakdownSegment = z.object({
  label: z.string(),
  value: z.number(),
  // Optional explicit segment colour (Space workflow colour for the task-status
  // donut). Falls back to the page's default palette when absent.
  color: z.string().optional(),
})

export const analyticsSummaryResponse = z.object({
  kpis: analyticsKpis,
  breakdowns: z
    .object({
      taskStatus: z.array(analyticsBreakdownSegment).optional().default([]),
      goals: z.array(analyticsBreakdownSegment).optional().default([]),
    })
    .optional()
    .default({ taskStatus: [], goals: [] }),
  // Departments of the single company in scope (empty for the all-companies view),
  // used to populate the department filter dropdown.
  departments: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .optional()
    .default([]),
  scope: z
    .object({
      mode: z.string().optional(),
      tenantCount: z.number().optional(),
      departmentId: z.string().nullable().optional(),
    })
    .optional(),
})

// Normal vs Repeated (recurring-occurrence) task split — group totals plus the
// completed/open/overdue breakdown, added to every company/employee row and the
// summary KPIs. normalTotal + repeatedTotal === tasksTotal. Optional/defaulted
// so older API payloads still parse.
const analyticsSplitCounts = {
  normalTotal: z.number().optional().default(0),
  normalCompleted: z.number().optional().default(0),
  normalOpen: z.number().optional().default(0),
  normalOverdue: z.number().optional().default(0),
  repeatedTotal: z.number().optional().default(0),
  repeatedCompleted: z.number().optional().default(0),
  repeatedOpen: z.number().optional().default(0),
  repeatedOverdue: z.number().optional().default(0),
}

export const analyticsCompanyRow = z.object({
  id: z.string(),
  company: z.string().nullable().optional(),
  slug: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  subscriptionStatus: z.string().nullable().optional(),
  planTier: z.string().nullable().optional(),
  employees: z.number().optional().default(0),
  tasksTotal: z.number().optional().default(0),
  tasksCompleted: z.number().optional().default(0),
  tasksOpen: z.number().optional().default(0),
  tasksOverdue: z.number().optional().default(0),
  completionRate: z.number().optional().default(0),
  goalsTotal: z.number().optional().default(0),
  goalsCompleted: z.number().optional().default(0),
  reportsSubmitted: z.number().optional().default(0),
  verificationPending: z.number().optional().default(0),
  ...analyticsSplitCounts,
})

export const analyticsCompaniesResponse = z.object({
  data: z.array(analyticsCompanyRow),
  pagination: paginationMeta.optional(),
})

export const analyticsEmployeeRow = z.object({
  id: z.string(),
  employee: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  workRole: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  tasksTotal: z.number().optional().default(0),
  tasksCompleted: z.number().optional().default(0),
  tasksOpen: z.number().optional().default(0),
  tasksOverdue: z.number().optional().default(0),
  goalsTotal: z.number().optional().default(0),
  goalsCompleted: z.number().optional().default(0),
  reportsSubmitted: z.number().optional().default(0),
  verificationPending: z.number().optional().default(0),
  ...analyticsSplitCounts,
  lastLoginAt: z.string().nullable().optional(),
})

export const analyticsEmployeesResponse = z.object({
  data: z.array(analyticsEmployeeRow),
  pagination: paginationMeta.optional(),
})
